<script lang="ts" setup>
import AUApi from "@/api";
import type { AxiosResponse } from "axios";
import { ref, onMounted, onUnmounted, provide, nextTick, watch, computed } from "vue";
import { onBeforeRouteLeave } from "vue-router";

import { usePendingDienstplanSaves } from "@/components/usePendingDienstplanSaves";
import Constants from "@/Constants";
import { findSperrDate } from "@/SperreHelper";
import { getWeekdayNamesShort, getMonthNames } from "@/editor/CalenderHelper";
import { createElement, setCulture } from "@syncfusion/ej2-base";
import { loadCldr, L10n } from "@syncfusion/ej2-base";
import gregorian from "@syncfusion/ej2-cldr-data/main/de/ca-gregorian.json";
import numbers from "@syncfusion/ej2-cldr-data/main/de/numbers.json";
import timeZoneNames from "@syncfusion/ej2-cldr-data/main/de/timeZoneNames.json";
import numberingSystems from "@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json";
// EN CLDR Daten für korrekte englische Anzeige im Month-View
import gregorianEn from "@syncfusion/ej2-cldr-data/main/en/ca-gregorian.json";
import numbersEn from "@syncfusion/ej2-cldr-data/main/en/numbers.json";
import timeZoneNamesEn from "@syncfusion/ej2-cldr-data/main/en/timeZoneNames.json";

// Beide Locales vorab laden (DE und EN); Schedule nutzt :locale zur Auswahl
loadCldr(
  numberingSystems,
  gregorian,
  numbers,
  timeZoneNames,
  gregorianEn,
  numbersEn,
  timeZoneNamesEn
);
import CalcButton from "@/components/CalcButton.vue";
import CommonBaseFilter from "@/components/common//BaseFilter.vue";
import BaseUserSettings from "@/views/Base/UserSettings.vue";

import {
  getKZValue,
  getKZValueDisplay,
  getKZTitle,
  getKennungValue,
  getKZColumn,
} from "@/DataHelper";
import {
  globalsBegriffMemo,
  globalsBezeichnung,
  globalsKennung,
  begriffBezeichnung,
  checkBegriff,
} from "@/local";
import { Globals } from "@/Globals";
import {
  ScheduleComponent as EjsSchedule,
  ViewsDirective as EViews,
  ViewDirective as EView,
  ResourcesDirective as EResources,
  ResourceDirective as EResource,
  Day,
  Week,
  WorkWeek,
  Month,
  Year,
  Agenda,
  TimelineMonth,
  TimelineViews,
  TimelineYear,
  ScheduleModel,
  ScheduleComponent,
  Resize,
} from "@syncfusion/ej2-vue-schedule";
import { HeaderRenderer, ViewBase } from "@syncfusion/ej2-schedule";
import { showDialog as openDialog, showSnackbar } from "@/UIHelper";
import {
  onShowSite,
  registerBackCallback,
  unregisterBackCallback,
} from "@/NavigationHelper";

const patchedHeaderRenderer = HeaderRenderer as typeof HeaderRenderer & {
  __auEmptyRenderDatesGuard?: boolean;
};
const patchedViewBase = ViewBase as typeof ViewBase & {
  __auDateRangeGuard?: boolean;
};

if (!patchedHeaderRenderer.__auEmptyRenderDatesGuard) {
  const originalPreviousNextIconHandler =
    HeaderRenderer.prototype.previousNextIconHandler;
  HeaderRenderer.prototype.previousNextIconHandler = function (this: any) {
    const headerRenderer = this as any;
    const dates =
      headerRenderer.parent.currentView === "Agenda"
        ? [headerRenderer.parent.getCurrentViewDates()[0]]
        : headerRenderer.parent.getCurrentViewDates();

    if (!dates || dates.length === 0 || !dates[0] || !dates[dates.length - 1]) {
      headerRenderer.setCalendarMinMaxDate();
      return;
    }

    return originalPreviousNextIconHandler.call(headerRenderer);
  };
  patchedHeaderRenderer.__auEmptyRenderDatesGuard = true;
}

if (!patchedViewBase.__auDateRangeGuard) {
  const originalGetDateRangeText = ViewBase.prototype.getDateRangeText;
  const originalFormatDateRange = ViewBase.prototype.formatDateRange;

  if (
    typeof originalGetDateRangeText !== "function" ||
    typeof originalFormatDateRange !== "function"
  ) {
    // Fehlerausgabe und kein Patchen, um Minify-Fehler zu vermeiden
    // eslint-disable-next-line no-console
    console.error(
      "[baseSyncSchedule] Patch-Fehler: Original-Methoden sind keine Funktionen!",
      {
        originalGetDateRangeText,
        originalFormatDateRange,
      }
    );
  } else {
    ViewBase.prototype.getDateRangeText = function (
      this: any,
      date?: Date,
      dateCollection?: Date[]
    ) {
      const safeDateCollection = Array.isArray(dateCollection)
        ? dateCollection.filter((value) => value instanceof Date)
        : Array.isArray(this.renderDates)
        ? this.renderDates.filter((value: unknown) => value instanceof Date)
        : [];
      const fallbackDate =
        date instanceof Date ? date : this.parent?.selectedDate;

      if (safeDateCollection.length === 0) {
        const safeStartDate =
          fallbackDate instanceof Date ? fallbackDate : new Date();
        const view =
          this.parent?.currentView || this.parent?.activeView?.options?.option;

        if (view === "WorkWeek") {
          let weekNumber = null;
          if (
            safeStartDate &&
            typeof safeStartDate.getWeekNumber === "function"
          ) {
            weekNumber = safeStartDate.getWeekNumber();
          } else {
            const d = new Date(
              Date.UTC(
                safeStartDate.getFullYear(),
                safeStartDate.getMonth(),
                safeStartDate.getDate()
              )
            );
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            weekNumber = Math.ceil(
              ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
            );
          }
          return `KW ${weekNumber}: ${originalFormatDateRange.call(
            this,
            safeStartDate,
            safeStartDate
          )}`;
        }

        return originalFormatDateRange.call(this, safeStartDate, safeStartDate);
      }

      let safeStartDate = date instanceof Date ? date : safeDateCollection[0];
      if (!(safeStartDate instanceof Date) || isNaN(safeStartDate.getTime())) {
        safeStartDate = new Date();
      }
      // Wochennummer für Week/TimelineWeek/WorkWeek ergänzen
      const view =
        this.parent?.currentView || this.parent?.activeView?.options?.option;
      if (view === "Week" || view === "TimelineWeek" || view === "WorkWeek") {
        // getWeekNumber aus CalenderHelper
        let weekNumber = null;
        if (
          safeStartDate &&
          typeof safeStartDate.getWeekNumber === "function"
        ) {
          weekNumber = safeStartDate.getWeekNumber();
        } else {
          // Fallback: ISO-Woche berechnen
          const d = new Date(
            Date.UTC(
              safeStartDate.getFullYear(),
              safeStartDate.getMonth(),
              safeStartDate.getDate()
            )
          );
          const dayNum = d.getUTCDay() || 7;
          d.setUTCDate(d.getUTCDate() + 4 - dayNum);
          const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
          weekNumber = Math.ceil(
            ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
          );
        }
        const orig = originalGetDateRangeText.call(
          this,
          safeStartDate,
          safeDateCollection
        );
        return `KW ${weekNumber}: ${orig}`;
      }
      return originalGetDateRangeText.call(
        this,
        safeStartDate,
        safeDateCollection
      );
    };

    ViewBase.prototype.formatDateRange = function (
      this: any,
      startDate?: Date,
      endDate?: Date
    ) {
      const fallbackDate =
        this.parent?.selectedDate instanceof Date
          ? this.parent.selectedDate
          : new Date();
      const safeStartDate =
        startDate instanceof Date ? startDate : fallbackDate;
      const safeEndDate = endDate instanceof Date ? endDate : safeStartDate;
      return originalFormatDateRange.call(this, safeStartDate, safeEndDate);
    };
  }
  patchedViewBase.__auDateRangeGuard = true;
}

provide("schedule", [
  Day,
  Week,
  WorkWeek,
  Month,
  Year,
  Agenda,
  TimelineMonth,
  TimelineViews,
  TimelineYear,
  Resize,
]);
const emit = defineEmits<{
  loaded: [valid?: boolean, goTo?: string];
  alert: [text: string, type: "error"];
}>();
const props = defineProps<{
  editorComponent?: string;
  query?: string;
  editorComponent1?: string;
  readOnly?: boolean;
  currentView?: string;
  showMonth?: boolean;
  showTimeLineMonth?: boolean;
  showTimeLineYear?: boolean;
  showDay?: boolean;
  readBewilligt?: boolean;
  onlyEdit?: boolean;
  abteilung?: boolean;
  showWeek?: boolean;
  showQuickInfo?: boolean;
  gotoToday?: boolean;
  gotoAic?: number; // darf nicht auf NUMBEr geändert werden - sonst geht das hinscrollen nicht mehr?
  dialogAicStamm?: boolean;
  saveQuery?: string;
  saveModell?: string; // prüft VOR dem Speichern auf Doppelbuchungen?
  abwZeitraum?: any;
  showGroup?: boolean;
  allowDragAndDrop?: boolean;
  formular?: ALLFrame;
  setMinDate?: boolean;
  useCellTemplate?: boolean;
  persistence?: boolean;
  reloadGroup?: boolean;
  reload?: boolean;
  anzeigeStunden?: boolean;
  groupUebergabe?: any;
  showICS?: boolean;
  group2?: string;
  queryMitarbeiter?: string | undefined;
  dienstplan?: boolean;
  vgSicht?: boolean;
}>();
export declare interface Mitarbeiter {
  aic: number;
  bezeichnung: string;
  favorite?: boolean;
}
let group: any = { resources: [] };
let employee: Array<{
  GroupID: number;
  Text: string;
  CssClass: string;
  Color: string;
  groupIDField: string;
  Designation: string;
  avatar: string;
  showImage: boolean;
  showAvatar: boolean;
  ZusatzText: string;
  Urlaub: string;
  Daten: any;
  startHour: string;
  endHour: string;
  negativ: number;
  workDays: number[];
  gesamtsaldo?: string;
  saldo?: string;
  // Icon: string;
}> = [];
let employeeOriginal: Array<{
  GroupID: number;
  Text: string;
  CssClass: string;
  Color: string;
  groupIDField: string;
  Designation: string;
  avatar: string;
  showImage: boolean;
  showAvatar: boolean;
  ZusatzText: string;
  Urlaub: string;
  Daten: any;
  startHour: string;
  endHour: string;
  negativ: number;
  workDays: number[];
  // Icon: string;
}> = [];
let groupData: Array<{
  GroupID: number;
  Text: string;
  Color: string;
  Expanded?: boolean;
  Daten: any;
}> = [];
let allowDragAndDropMutable = ref<boolean>(false);
let showQuickInfoMutable = ref<boolean>(false);
let readOnlyMutable = ref<boolean>(false);
let activeView = ref<any>("Month");
let showLoading = ref<boolean>(false);
let header: QueryHeader = { bezeichnung: " -- " };
let queryAIC: number = 0;
let initialized = ref<boolean>(false);
let myHeight = ref<number>(600);
let minDate: Date | null = new Date(1970, 0, 1);

let allColumns: DataColumn[] = [];
let zeitbereich: APIZeitbereich = AUApi.Current.zeitbereich;
let eventZeitbereich: any;
let rowdata: QueryDataRow[] = [];
let sperrDate = ref<Date>(new Date(1970, 0, 1));
let allowMultiple = ref<boolean>(true);
let showAbteilung = ref<boolean>(true);
let groupAic: string = "";
let groupBegriff: string = "";
let checked = ref<boolean>(false);
let persistedGroupState = ref<boolean | null>(null);

let allowDeleting = ref<boolean>(true);
let showFilter = ref<boolean>(false);
let filteredData: any[] = [];
let goToAicLocal: number = props.gotoAic ?? 0;
let localDate: Date | null = new Date(1970, 0, 1);
// dataBound feuert bei aktivierter Gruppierung (Ressourcen-Virtualisierung) laufend beim
// Scrollen mit - ohne diese Signaturen würde onDataBound() bei jedem dataBound erneut zum
// gleichen Ziel zurückscrollen und die vertikale Scrollbar dabei ständig zurückspringen.
let lastTimelineWeekScrollSignature: string | null = null;
let lastGotoResourceScrollSignature: string | null = null;
let lastWeekViewScrollSignature: string | null = null;
// Nach "Alle speichern" feuert dataBound - bedingt durch Ressourcen-Virtualisierung/Settle
// der neu gemounteten Instanz - oft noch ein zweites Mal kurz nach dem ersten (bei dem wir
// pendingScheduleScrollRestore anwenden). pendingScheduleScrollRestore ist dann schon
// konsumiert (null), die "restoredPendingScrollPosition"-Sperre in onDataBound() gilt aber
// nur innerhalb DES EINEN Aufrufs - ohne dieses Zeitfenster würde der zweite Aufruf die
// eben wiederhergestellte Position sofort wieder zur Default-Position zurückspringen lassen.
let scrollRestoreSuppressUntil = 0;
// Ziel-Scrollposition, solange scrollRestoreSuppressUntil noch in der Zukunft liegt - wird
// bei jedem dataBound-Aufruf innerhalb des Fensters erneut angewendet (nicht nur einmal),
// falls Syncfusions eigenes Re-Layout (Virtualisierung/Settle) die Position sonst zurücksetzt.
let lastRestoredScrollPosition: { scrollTop: number; scrollLeft: number } | null = null;
let showSpringer = ref<boolean>(false);
let springer: Mitarbeiter[] = [];
let missingGroupFallbackId: number | null = null;
let allowedExtensions: any = ".ics";
let buttons: any = {
  browse: "Choose file",
};
let dienstplanAnzeige = ref<string>("");

let timeScale = ref<any>({ enable: false });
// let workHours: any;
let workDays = ref<number[]>([]);

let showWeekNumber = ref<boolean>(false);
let showGroupMutable = ref<boolean>(false);
let dateFormat: string = "dd.MM.yyyy";
let allowResizing = ref<boolean>(true);
let showDialog = ref<boolean>(false);
let von: any = "";
let bis: any = "";
let Subject: any = "";
let bewilligung = ref<boolean>(false);
let bewilligt = ref<boolean>(false);
let offen = ref<boolean>(false);
let htmText: string = "";
let showFullWidth = ref<boolean>(false);
let dialog: any = {
  zeitbereich: null,
  item: null,
  aic: null,
  editorComponent: "",
  groupID: null,
  groupName: "",
  abwesenheit: false,
  pendingDienstplanSaveId: null,
  preloadedData: null,
};
let showText = ref<boolean>(false);
let avatarIcon: any;
let savedGroupIndex: any;
let savedGroupAic: any;
let currentGroupIndex: any;
const queryData = ref<QueryResponse | null>(null);
const filterKey = ref<number>(0);
let printItems!: ALLDruck[] | undefined;
let modellItems!: ALLModell[] | undefined;
const showCalcMenu = ref<boolean>(false);
const calcMenuPersistent = ref<boolean>(false);
let startHour = ref<string>("08:00");
let endHour = ref<string>("20:00");
let startHourShow: string = "08:00";
let vector: number[] = [];
let selectedDate = ref<Date>(new Date());
let eventSettings = ref<{
  dataSource: any[];
  enableTooltip?: boolean;
  allowDeleting?: boolean;
  fields?: any;
  eventTemplate?: string;
}>({
  dataSource: [],
});

function getGroupStateStorageKey() {
  if (!props.group2) {
    return null;
  }

  const queryKey = props.queryMitarbeiter ?? props.query ?? "";
  const formKey = props.formular?.aic ?? "";
  const userKey = AUApi.Current.user?.id ?? "0";
  return [
    "baseSyncScheduleGroupState",
    userKey,
    queryKey,
    props.group2,
    formKey,
    props.dienstplan ? "dienstplan" : "default",
  ].join("_");
}

function getExpandedGroupsStorageKey() {
  const baseKey = getGroupStateStorageKey();
  return baseKey ? `${baseKey}_expanded` : null;
}

function loadPersistedGroupState() {
  const key = getGroupStateStorageKey();
  if (!key || typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(key);
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
}

function persistGroupState(value: boolean) {
  persistedGroupState.value = value;

  const key = getGroupStateStorageKey();
  if (!key || typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, value ? "true" : "false");
}

function loadPersistedExpandedGroups(): Record<string, boolean> {
  const key = getExpandedGroupsStorageKey();
  if (!key || typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistExpandedGroups(expandedGroups: Record<string, boolean>) {
  const key = getExpandedGroupsStorageKey();
  if (!key || typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(expandedGroups));
}

function setPersistedExpandedGroup(
  groupId: string | number,
  expanded: boolean
) {
  const normalizedGroupId = String(groupId);
  const expandedGroups = loadPersistedExpandedGroups();
  expandedGroups[normalizedGroupId] = expanded;
  persistExpandedGroups(expandedGroups);

  const localGroup = groupData.find(
    (groupItem) => String(groupItem.GroupID) === normalizedGroupId
  );
  if (localGroup) {
    localGroup.Expanded = expanded;
  }
}

function getPersistedExpandedGroup(groupId: string | number) {
  const expandedGroups = loadPersistedExpandedGroups();
  const value = expandedGroups[String(groupId)];
  return typeof value === "boolean" ? value : null;
}

function resolveGroupResourceId(value: any, fallbackIndex?: number) {
  if (value !== undefined && value !== null && value !== "") {
    return value;
  }

  if (fallbackIndex !== undefined) {
    const syntheticGroupId = -1000000 - fallbackIndex;
    if (missingGroupFallbackId === null) {
      missingGroupFallbackId = syntheticGroupId;
    }
    return syntheticGroupId;
  }

  if (missingGroupFallbackId !== null) {
    return missingGroupFallbackId;
  }

  missingGroupFallbackId = -1000000;
  return missingGroupFallbackId;
}

let refillRunning: boolean = false;
let pendingRefreshAfterReload = false;
let showSchedule = ref<boolean>(false);
const fixedTimelineScrollLeft = ref<number>(0);
const fixedTimelineScrollMax = ref<number>(0);
let filterColumns: FilteredDataColumn[] | null = null;

let lastDt: string | null = null;
let lastBereich: string = "Tag";
let cssDesign = ref<string>("dvh");
let scheduleObj = ref(null);
const firstMonthValue = ref<number>(0);
const monthsCount = ref<number>(12);
const localeRef = ref<string>(AUApi.Current.user.bezSprache || "de");
const showFixedTimelineScrollbar = computed(
  () => props.dienstplan && activeView.value === "TimelineWeek"
);
const hasPendingDienstplanSaves = computed(
  () => props.dienstplan && pendingDienstplanSaves.value.length > 0
);
const dienstplanPendingLayoutOffset = computed(() =>
  hasPendingDienstplanSaves.value ? 76 : 0
);
const scheduleHeight = computed(() =>
  Math.max(320, myHeight.value - dienstplanPendingLayoutOffset.value)
);
function normalizeLocale(input?: string): string {
  const v = (input || "de").toLowerCase();
  if (v.startsWith("de")) return "de";
  if (v.startsWith("en")) return "en";
  return "de";
}
let setCompKey = ref<number>(0);
const showFilterComp = ref<boolean>(true);
let showRefresh = ref<boolean>(false);
// unterdrückt den verzögerten scrollToResource()-Aufruf in fillUpEvents() (finally-Block)
// während "Alle speichern" die zuvor gemerkte Scroll-Position gerade wiederhergestellt hat
let suppressAutoResourceScroll = ref<boolean>(false);

const {
  pendingDienstplanSaves,
  savingPendingDienstplan,
  savingPendingDienstplanDone,
  savingPendingDienstplanTotal,
  pendingDienstplanFocusEvent,
  shouldFocusPendingDienstplanOnDataBound,
  pendingScheduleScrollRestore,
  reloadPreservingScroll,
  getScheduleDataSource,
  refreshScheduleDataSourceWithPending,
  closeQueuedDienstplanDialog,
  retryScrollPendingDienstplanEventIntoView,
  loadPendingDienstplanSaves,
  queueDienstplanSave,
  discardPendingDienstplanSaves,
  discardPendingDienstplanSave,
  savePendingDienstplanSaves,
} = usePendingDienstplanSaves({
  dienstplan: () => props.dienstplan,
  query: () => props.query,
  queryMitarbeiter: () => props.queryMitarbeiter,
  rowdata: () => rowdata,
  employee: () => employee,
  getRowMatchAic: (row: any) => {
    if (row?.abwesenheit) {
      const abwAic = Number(getKennungValue(row, "WEBPLANUNGBEWBEW", allColumns));
      if (abwAic) {
        return abwAic;
      }
    }
    return Number(row?.aic_Bew_pool);
  },
  dialog,
  goToAicLocal: {
    get: () => goToAicLocal,
    set: (value: number) => {
      goToAicLocal = value;
    },
  },
  localDate: {
    set: (value: Date) => {
      localDate = value;
    },
  },
  getCurrentEmployeeResourceId,
  getScheduleInstance,
  selectedDate,
  startHour,
  startHourShow,
  eventSettings,
  showDialog,
  showRefresh,
  showLoading,
  updateTimelineScrollbar: () => refreshFixedTimelineScrollbar(),
  refreshScheduleData: (skipRemount?: boolean) => refreshScheduleData(undefined, skipRemount),
  suppressAutoResourceScroll,
});

// beim Verlassen dieser Seite (Navigation weg oder Logout, da dieser über
// router.replace läuft) bei noch nicht gespeicherten lokalen Dienstplan-Vormerkungen nachfragen
onBeforeRouteLeave((_to, _from, next) => {
  if (!props.dienstplan || pendingDienstplanSaves.value.length === 0) {
    next();
    return;
  }
  const count = pendingDienstplanSaves.value.length;
  const wantsSave = confirm(
    `Es ${count === 1 ? "ist" : "sind"} noch ${count} lokal vorgemerkte${
      count === 1 ? "r" : ""
    } Dienstplan-Eintrag${count === 1 ? "" : "e"} nicht gespeichert. Jetzt speichern?`
  );
  if (wantsSave) {
    savePendingDienstplanSaves().finally(() => next());
  } else {
    discardPendingDienstplanSaves();
    next();
  }
});

watch(
  () => queryData.value,
  async (nv, ov) => {
    if (nv !== ov) {
      showFilterComp.value = false; // unmount
      await nextTick(); // DOM flush
      filterKey.value += 1; // neuer Key erzwingt neuen Mount
      showFilterComp.value = true; // wieder anzeigen
    }
  }
);
watch(activeView, () => {
  // ein echter View-Wechsel muss die Auto-Scroll-Position immer neu setzen dürfen, auch
  // wenn Datum/Resource unverändert sind (die dataBound-Signaturen würden das sonst
  // fälschlich als "schon gescrollt" ansehen und blockieren)
  lastTimelineWeekScrollSignature = null;
  lastGotoResourceScrollSignature = null;
  lastWeekViewScrollSignature = null;
});
function getSchedule(): any {
  return document.querySelector(".e-schedule");
}

function getScheduleInstance(): any {
  const scheduleElement = getSchedule() as any;
  const instance = scheduleElement?.ej2_instances?.[0];
  return instance?.ej2Instances ?? instance ?? null;
}

function getCurrentEmployeeResourceId(): any {
  const scheduleInstance = getScheduleInstance();
  const groupIndex = scheduleInstance?.uiStateValues?.groupIndex;
  const resourceDetails = scheduleInstance?.getResourcesByIndex?.(groupIndex);
  return (
    resourceDetails?.resourceData?.GroupID ??
    resourceDetails?.groupData?.GroupID ??
    null
  );
}
L10n.load({
  de: {
    schedule: {
      day: "Tag",
      week: "Woche",
      workWeek: "Arbeitswoche",
      month: "Monat",
      agenda: "Agenda",
      today: "Heute",
      noEvents: "Keine Ereignisse",
      emptyContainer: "Keine Termine an diesem Tag.",
      allDay: "Ganztägig",
      start: "Beginn",
      end: "Ende",
      more: "mehr",
      close: "Schließen",
      cancel: "Abbrechen",
      save: "Speichern",
      delete: "Löschen",
      newEvent: "Neues Ereignis",
      timelineDay: "Zeitachse Tag",
      timelineWeek: "Zeitachse Woche",
      timelineWorkWeek: "Arbeitswoche",
      timelineMonth: "Monat",
      timelineYear: "Jahr",
    },
  },
});

function updateMyHeight() {
  myHeight.value = window.innerHeight - 100;
  if (showGroupMutable.value) {
    myHeight.value = window.innerHeight - 220;
  }
  // Fenstergröße geändert - Fenster-Innenmaße von .e-content-wrap ändern sich mit, die
  // gemerkten fixen Scrollbar-Werte (scrollWidth/scrollHeight vs. client*) aber nicht von
  // allein - ohne das hier blieben die fixen Balken bis zur nächsten Scroll-/dataBound-
  // Aktion auf dem alten Stand ("Scrollbar muss erst manuell aufgefrischt werden").
  refreshFixedTimelineScrollbar();
}

function navigationBackCallback() {
  if (showDialog.value) {
    dialogBack(null);
    return true;
  }
  return false;
}


onMounted(async () => {
  //registerBackCallback for hiding dialog on back button
  registerBackCallback(navigationBackCallback);

  loadPendingDienstplanSaves();

  const findSpringer = checkBegriff("Web_SpringerSetzen");
  if (findSpringer) {
    showSpringer.value = true;
  }
  if (AUApi.Current.zeitbereich) {
    selectedDate.value = AUApi.Current.zeitbereich.von;
  }
  // Kultur für Syncfusion gemäß Benutzersprache setzen (normalisiert)
  localeRef.value = normalizeLocale(AUApi.Current.user.bezSprache || "de");
  try {
    setCulture(localeRef.value);
  } catch (e) {
    console.warn("setCulture fehlgeschlagen", e);
  }
  if (props.dienstplan) {
    cssDesign.value = "dvhDienstplan";
  }
  activeView.value = props.currentView ?? "Month";
  showGroupMutable.value = props.showGroup;
  persistedGroupState.value = loadPersistedGroupState();
  if (persistedGroupState.value !== null) {
    checked.value = persistedGroupState.value;
    showAbteilung.value = persistedGroupState.value;
  }
  showQuickInfoMutable.value = props.showQuickInfo;
  readOnlyMutable.value = props.readOnly;
  allowDragAndDropMutable.value = props.allowDragAndDrop;
  if (activeView.value === "Week" || activeView.value === "WorkWeek") {
    timeScale.value = { interval: 60, slotCount: 1 };
    workDays.value = normalizeWorkDays([1, 2, 3, 4, 5, 6, 7]);
  } else if (
    activeView.value === "TimelineWeek" &&
    (props.dienstplan || props.anzeigeStunden)
  ) {
    // checked = true;
    // nicht gruppiert ist die Darstellung aktuell falsch...
    timeScale.value = { interval: 120, slotCount: 1 };
  } else {
    timeScale.value = { enable: false };
  }
  if (props.abwZeitraum) {
    let von: any;
    if (props.abwZeitraum.neuerZeitbereich) {
      von = props.abwZeitraum.neuerZeitbereich;
    } else {
      von = props.abwZeitraum;
    }
    if (von) {
      // selectedDate.value = von;
      setSelectedDate(von);
    }

    if (props.abwZeitraum) {
      let bis = new Date();
      if (
        activeView.value === "Week" ||
        activeView.value === "TimelineWeek" ||
        activeView.value == "WorkWeek"
      ) {
        von = new Date(von.firstOfWeek().setHours(0, 0, 0, 0));
        selectedDate.value = von;
        bis = von.addDays(+6);
      } else if (
        activeView.value === "Month" ||
        activeView.value === "TimelineMonth"
      ) {
        const dateBeginn = new Date(
          props.abwZeitraum.firstOfMonth().setHours(0, 0, 0, 0)
        );
        selectedDate.value = dateBeginn;
        von = new Date(von.firstOfMonth().setHours(0, 0, 0, 0));
        bis = new Date(von.lastInMonth());
        von = getFirstDayOfWeek(von);
      } else {
        von = new Date(von.firstOfMonth().setHours(0, 0, 0, 0));
        selectedDate.value = von;
        bis = new Date(von.lastInMonth());
      }
      zeitbereich = {
        von,
        bis,
        bereich: "Tag",
      };
    }
  }
  if (!zeitbereich) {
    // wenn CurrentView Woche ist - dann soll er auch nur für diese Woche die daten laden
    // und nicht für den gesamt eingestellten zeitraum
    if (
      activeView.value === "Week" ||
      activeView.value === "TimelineWeek" ||
      activeView.value == "WorkWeek"
    ) {
      von = getFirstDayOfWeek(von);
      selectedDate = von;
      bis = von.addDays(+6); // nur 6 Tage - da BIS INkl!
      zeitbereich = {
        von,
        bis,
        bereich: "Woche",
      };
    } else {
      zeitbereich = AUApi.Current.zeitbereich;
      // zusätzlich auf den ersten Tag der Woche setzen! damit Vortage auch sauber geholt und angezeigt werden
      zeitbereich.von = getFirstDayOfWeek(zeitbereich.von);
    }
  }
  // if (activeView.value === "Week" || activeView.value === "TimelineWeek") {
  //   // onZeitraum();
  // }
  updateMyHeight();
  window.addEventListener("resize", updateMyHeight);

  if (Constants.isMobile) {
    showWeekNumber.value = false;
    allowDragAndDropMutable.value = false;
    showFullWidth.value = true;
    // Delete muss in QuickInfo weggeschalten werden
    if (readOnlyMutable.value || props.onlyEdit) {
      allowDeleting.value = false;
    }
  }
  if (props.formular) {
    modellItems = props.formular.mod2 ?? [];
    printItems = props.formular.druck ?? [];
  }
  if (props.gotoToday) {
    selectedDate.value = new Date();
  }
  if (props.queryMitarbeiter) {
    if (props.group2) {
      loadResources(true);
    } else {
      loadResources(false);
    }
  } else {
    reloadData();
  }
  getSperrDate();
});

onUnmounted(() => {
  //remove back callback
  unregisterBackCallback(navigationBackCallback);
  window.removeEventListener("resize", updateMyHeight);
});

function getFirstDayOfWeek(date: Date): Date {
  const dayOfWeek = date.getDay();
  const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

/**
 * Schreibt selectedDate nur, wenn sich der Zeitpunkt tatsächlich ändert.
 * Grund: selectedDate ist per :selectedDate="selectedDate" einseitig in <ejs-schedule> gebunden.
 * Syncfusion ruft bei jeder Property-Änderung intern changeDate() auf, das wiederum das
 * "navigating"-Event erneut feuert (siehe ej2-schedule/schedule.js propertyChangeAction/changeDate).
 * Ohne diesen Wertevergleich erzeugt jede Neuzuweisung (auch mit identischem Zeitpunkt, aber
 * neuem Date-Objekt) einen weiteren navigating-Zyklus -> Endlosschleife/Hänger bei z.B. "Heute".
 */
function setSelectedDate(newDate: Date | null | undefined) {
  if (!newDate) {
    return;
  }
  if (selectedDate.value && selectedDate.value.getTime() === newDate.getTime()) {
    return;
  }
  console.log(
    "DEBUG setSelectedDate: " +
      selectedDate.value?.toString() +
      " -> " +
      newDate.toString()
  );
  console.trace();
  selectedDate.value = newDate;
}

/** Für TimelineWeek: heute wenn in der angezeigten Woche, sonst Montag */
function getTimelineWeekScrollTarget(weekBase: Date): Date {
  const monday = getFirstDayOfWeek(new Date(weekBase));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= monday && today <= sunday ? today : monday;
}
function reloadNewZeitbereich(vonNavigation?: boolean) {

  // Verhindere Re-Entrys während eines laufenden Reloads
  if (refillRunning) {
    console.log("reloadNewZeitbereich übersprungen – Reload läuft bereits");
    return;
  }
  const schedule = getScheduleInstance();

  let von = new Date(selectedDate.value);
  let bis = new Date(selectedDate.value.lastInMonth());
  if (schedule) {
    savedGroupIndex = schedule.uiStateValues?.groupIndex;
    // Identität (GroupID) statt nacktem Index merken - die employee-Liste kann sich
    // zwischen altem und neuem Zeitraum in Reihenfolge/Zusammensetzung unterscheiden
    // (zB weniger/andere MA im neuen Zeitraum), wodurch derselbe Index nach dem Reload
    // auf einen anderen MA zeigen würde (beobachtet beim Zurück-Navigieren).
    savedGroupAic = getCurrentEmployeeResourceId();
  }
  let bereich = "Tag";
  if (!vonNavigation) {
    if (props.currentView === "TimelineWeek") {
      activeView.value = "TimelineWeek";
      von = getFirstDayOfWeek(von);
      // von = von.firstOfWeek();
      setSelectedDate(von);
      bis = von.addDays(+6);
      bereich = "Woche";
    } else {
      activeView.value = "Week";
      von = getFirstDayOfWeek(von);
      // von = von.firstOfWeek();
      setSelectedDate(von);
      bis = von.addDays(+6);
      bereich = "Woche";
    }
  }
  if (vonNavigation) {
    if (activeView.value === "TimelineYear" || activeView.value === "Year") {
      // von = new Date(von.getFullYear(), 1, 0);
      bis = new Date(bis.getFullYear(), 12, 0);
    } else if (activeView.value === "TimelineWeek") {
      activeView.value = "TimelineWeek";
      von = getFirstDayOfWeek(von);
      setSelectedDate(von);
      bis = von.addDays(+6);
      bereich = "Woche";
    } else if (activeView.value === "TimelineDay") {
      setSelectedDate(von);
      bis = von;
    } else if (activeView.value === "WorkWeek") {
      activeView.value = "WorkWeek";
      setSelectedDate(von);
      bis = von.addDays(+6);
      bereich = "Woche";
    } else if (activeView.value === "Week") {
      activeView.value = "Week";
      setSelectedDate(von);
      bis = von.addDays(+6);
      bereich = "Woche";
    } else {
      von = getFirstDayOfWeek(von);
    }
  }
  showSchedule.value = false;

  lastBereich = bereich;
  zeitbereich = {
    von,
    bis,
    bereich,
  };
  if (props.reloadGroup) {
    if (props.queryMitarbeiter) {
      if (props.group2) {
        loadResources(true, vonNavigation);
      } else {
        loadResources(false, vonNavigation);
      }
    } else {
      reloadData();
    }
  } else {
    reloadData();
  }
}

async function loadResources(
  gruppierung: boolean,
  vonNavigation?: boolean,
  skipRemount: boolean = false
) {
  try {
    console.log("loadResources()");
    if (props.queryMitarbeiter == null || props.queryMitarbeiter === "") {
      console.log("keine Ressourcen geladen");
      return;
    }
    let queryLocal = props.queryMitarbeiter;
    if (gruppierung && props.group2) {
      queryLocal = props.group2;
    }
    const vecStamm: any[] = [];
    const resource = await AUApi.Current.getQuery(
      queryLocal,
      undefined,
      queryAIC,
      zeitbereich,
      0,
      false,
      false,
      "0",
      props.formular?.aic,
      vecStamm
    );
    if (resource) {
      await fillUpResource(resource, gruppierung, vonNavigation, skipRemount);
    }
  } catch (e: any) {
    console.error("mounterror: " + e, e?.stack);
  }
}
async function fillUpResource(
  resource: any,
  gruppierung: boolean,
  vonNavigation?: boolean,
  skipRemount: boolean = false
) {
  showText.value = false;
  try {
    const rowdata = resource.data.data;
    if (rowdata && rowdata.length === 0) {
      if (rowdata.length === 0) {
        group = {
          enableCompactView: true,
          resources: [],
        };
        // schedule anzeigen, damit man den Zeitraum wechseln kann!
        // showText.value = true; // showText ist nicht wichtg - Zeitraum Auswahl muss immer da sein!
        if (activeView.value !== "TimelineWeek") {
          // bei TimelineWeek kommt da ein Error...
          showGroupMutable.value = false;
          if (props.dienstplan) {
            group = {
              enableCompactView: true,
              resources: [],
            };
          }
        }
        const init = true;
        showAbteilung.value = false;
        groupBegriff = "";
        groupData = [];
        employee = [];
        eventSettings.value = {
          dataSource: [],
          enableTooltip: false,
          allowDeleting: allowDeleting.value,
        };
        if (gruppierung) {
          showSnackbar("keine Daten für die Gruppierung gefunden");
        }
        showLoading.value = false;
        refillRunning = false;
        initialized.value = true;
        showSchedule.value = true; // Schedule muss immer da sein - sonst kann man den Zeitraum nicht wechseln
        emit("loaded", true);
      }
    } else {
      const allColumns = resource.data.columns;
      if (gruppierung) {
        groupData = [];
        missingGroupFallbackId = null;
        for (let i = 0; i < rowdata.length; i++) {
          try {
            const groupRow = rowdata[i];
            const groupId = resolveGroupResourceId(
              getKZValue(groupRow, "id", allColumns),
              i
            );
            const favorit = getKZValue(groupRow, "favorit", allColumns);
            let color = "green";
            if (favorit || !props.dienstplan) {
              color = "rgb(255, 140, 0)";
            }
            groupData.push({
              Text: getKZValueDisplay(groupRow, "text", allColumns),
              GroupID: groupId,
              Color: color,
              Expanded: getPersistedExpandedGroup(groupId) ?? true,
              Daten: 0,
            });
          } catch (e) {
            console.error(e);
          }
        }
        console.log("groupDatafertig");
        // if (!props.dienstplan) {
        //   checked.value = true;
        // }

        await loadResources(false, vonNavigation, skipRemount);
      } else {
        employee = [];
        // Ein vorheriger, verfrühter Fetch mit leerem rowdata (zB bevor zeitbereich fertig
        // war) kann showGroupMutable weiter oben (Zeile ~1103) dauerhaft auf false gesetzt
        // haben - ohne Reset hier bliebe das komplette e-resources-Employee-Sidebar dann für
        // den Rest des Lebenszyklus dieser Komponente ausgeblendet, obwohl employee/events ab
        // jetzt korrekt geladen werden. Bei jedem erfolgreichen Laden (rowdata vorhanden)
        // daher immer wieder aus props.showGroup neu setzen.
        showGroupMutable.value = props.showGroup;
        const gruppe = allColumns.filter(
          (c: any) => c.meta.gruppierbar === true
        );
        if (gruppe && gruppe.length > 0) {
          groupAic = gruppe[0].aic;
          if (persistedGroupState.value !== null) {
            checked.value = persistedGroupState.value;
          } else if (gruppe[0].meta.pGruppiert !== undefined) {
            checked.value = gruppe[0].meta.pGruppiert;
          } else {
            if (!vonNavigation) {
              checked.value = false;
            }
          }
          showAbteilung.value = checked.value;
          groupBegriff = resource.data.header.aicBegriff;
        } else {
          checked.value = false;
          showAbteilung.value = false;
          groupBegriff = "";
        }
        // Dieser (nicht gruppierte) Zweig läuft in 2 verschiedenen Situationen:
        // 1) als Folge-Aufruf am Ende des gruppierung===true Zweigs oben (props.group2 gesetzt) -
        //    dort wurde groupData (Zeile ~1147) bereits mit echten Abteilungen befüllt.
        // 2) als direkter Aufruf für eine reine Flach-Abfrage ohne group2 (zB Web_ListeMA_
        //    Stellvertreter, Web_ZE_Dienstplan_ListeMA) - hier bleibt groupData IMMER leer.
        // showAbteilung darf die GroupData-e-resource (v-if="showAbteilung") nur dann anzeigen,
        // wenn dafür auch echte Daten existieren - sonst kann Syncfusion die Employee-Ressource
        // (die per groupIDField auf GroupData verweist) nicht mehr auflösen und die komplette
        // Mitarbeiterliste verschwindet (betraf Fall 2: Stellvertreter, Dienstplan/mobil).
        if (groupData.length === 0) {
          showAbteilung.value = false;
        }
        vector = [];
        for (let i = 0; i < rowdata.length; i++) {
          try {
            const employeeRow = rowdata[i];
            const cssClass = "cssAlle";
            let color = "transparent";
            if (!props.useCellTemplate && !props.dienstplan) {
              color = "#fff";
            const extraColor = getKZValue(employeeRow, "color", allColumns);
            if (extraColor) {
              color = extraColor;
              }
            }
            if (props.formular?.pers) {
              if (i % 2 === 1) {
                color = "rgb(194 203 237)";
              }
            }
            let zusatz = getKZValue(employeeRow, "ZusatzText", allColumns);
            if (zusatz) {
              zusatz = "(" + zusatz + ")";
            }
            const img = getKZColumn("icon", allColumns);
            let showImage = false;
            let showAvatar = false;
            let avatar;
            if (img) {
              avatar = getKZValue(employeeRow, "icon", allColumns);

              if (avatar !== "" && avatar) {
                loadImage(avatar);
                avatar = avatarIcon;
                if (avatar === "mdi-account-circle") {
                  showAvatar = true;
                } else {
                  showImage = true;
                }
              } else {
                showAvatar = true;
                avatar = "mdi-account-circle";
              }
            }
            let sollzeitAnzeige = getKZValueDisplay(
              employeeRow,
              "Sollzeit",
              allColumns
            );
            if (sollzeitAnzeige) {
              const sollzeitText = getKZTitle(
                employeeRow,
                "Sollzeit",
                allColumns
              );
              sollzeitAnzeige = sollzeitText + " " + sollzeitAnzeige;
            }
            const arbeitszeitAnzeige = getKZValueDisplay(
              employeeRow,
              "Ist",
              allColumns
            );
            let urlaubAnzeige = getKZValueDisplay(
              employeeRow,
              "urlaub",
              allColumns
            );
            let gesamtSaldo = getKZValueDisplay(
              employeeRow,
              "Gesamtsaldo",
              allColumns
            );
            if (gesamtSaldo) {
              gesamtSaldo =
                getKZTitle(employeeRow, "Gesamtsaldo", allColumns) +
                ": " +
                gesamtSaldo;
            }
            // Gesamtsaldo2 == Gesamtsaldo inkl berücksichtigung geplanter Arbeitszeit
            let gesamtSaldo2 = getKZValueDisplay(
              employeeRow,
              "Gesamtsaldo2",
              allColumns
            );
            if (gesamtSaldo2) {
              gesamtSaldo2 =
                getKZTitle(employeeRow, "Gesamtsaldo2", allColumns) +
                ": " +
                gesamtSaldo2;
              gesamtSaldo = gesamtSaldo + " " + gesamtSaldo2;
            }
            if (urlaubAnzeige) {
              const urlaubText = getKZTitle(employeeRow, "urlaub", allColumns);
              if (!props.dienstplan) {
                urlaubAnzeige = urlaubText + ": " + urlaubAnzeige;
              }
            } else {
              urlaubAnzeige = null;
            }
            let negativ = 0;
            if (arbeitszeitAnzeige) {
              const arbeitsText = getKZTitle(employeeRow, "Ist", allColumns);
              urlaubAnzeige = getKZValueDisplay(
                employeeRow,
                "Saldo",
                allColumns
              );

              const urlaub = getKZValue(employeeRow, "Saldo", allColumns);
              if (urlaub > 0) {
                negativ = 1;
              }
              const titel = getKZTitle(employeeRow, "Saldo", allColumns);
              urlaubAnzeige = titel + ": " + urlaubAnzeige;
              sollzeitAnzeige =
                sollzeitAnzeige + " " + arbeitsText + " " + arbeitszeitAnzeige;
            }
            let workDaysLocal = getKZValue(employeeRow, "workDays", allColumns);
            const startHourLocal = getKZValue(
              employeeRow,
              "startHour",
              allColumns
            );
            if (startHourLocal) {
              startHour.value = startHourLocal;
            }
            const endHourLocal = getKZValue(employeeRow, "endHour", allColumns);
            if (endHourLocal) {
              endHour.value = endHourLocal;
            }

            let employeeWorkDays: number[];
            if (!workDaysLocal) {
              employeeWorkDays = normalizeWorkDays([0, 1, 2, 3, 4, 5, 6]);
            } else {
              employeeWorkDays = normalizeWorkDays(workDaysLocal);
            }
            vector.push(employeeRow.aic_Stamm);
            if (props.dienstplan) {
              if (props.readOnly) {
                // bin ich im pers. Dienstplan muss ich prüfen ob es ein Datum gibt ab dem die Daten angezeigt werden dürfen
                dienstplanAnzeige.value = getKennungValue(
                  employeeRow,
                  "DP_Freigabe",
                  allColumns
                );
              }
              employee.push({
                Text: getKZValueDisplay(employeeRow, "text", allColumns),
                GroupID: getKZValue(employeeRow, "id", allColumns),
                CssClass: cssClass,
                Color: color,
                groupIDField: resolveGroupResourceId(
                  getKZValue(employeeRow, "groupID", allColumns)
                ),
                Designation: sollzeitAnzeige,
                avatar,
                showImage,
                showAvatar,
                ZusatzText: zusatz,
                Urlaub: urlaubAnzeige,
                gesamtsaldo: gesamtSaldo,
                Daten: getKZValue(employeeRow, "count", allColumns),
                negativ,
                workDays: employeeWorkDays,
                startHour: startHour.value,
                endHour: endHour.value,
              });
            } else {
              employee.push({
                Text: getKZValueDisplay(employeeRow, "text", allColumns),
                GroupID: getKZValue(employeeRow, "id", allColumns),
                CssClass: cssClass,
                Color: color,
                groupIDField: resolveGroupResourceId(
                  getKZValue(employeeRow, "groupID", allColumns)
                ),
                Designation: getKZValueDisplay(
                  employeeRow,
                  "Designation",
                  allColumns
                ),
                avatar,
                showImage,
                showAvatar,
                ZusatzText: zusatz,
                Urlaub: urlaubAnzeige,
                Daten: getKZValue(employeeRow, "count", allColumns),
                negativ,
                workDays: employeeWorkDays,
                startHour: startHour.value,
                endHour: endHour.value,
              });
            }
          } catch (e) {
            console.error(e);
          }
        }
        employeeOriginal = [...employee];
        if (dienstplanAnzeige.value !== "") {
          let von = new Date(zeitbereich.von);
          von.setHours(0, 0, 0, 0); // Uhrzeit rausrechnen .. bleibt manchmal hängen..
          const dienstplan = new Date(dienstplanAnzeige.value);

          if (dienstplan.getTime() < von.getTime()) {
            showText.value = true;
            showSchedule.value = true;
            employee = [];

            showLoading.value = false;
            refillRunning = false;
            initialized.value = true;
            emit("loaded", true);
            return;
          }
          // wenn es daten gibt muss geprüft werden ob es in der Vergangenheit ist - nur dann dürfen Daten angezeigt werden
        }
        console.log("Employee Data fertig");
        if (employee && employee.length > 0) {
          await reloadData(skipRemount);
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}
async function reloadData(skipRemount: boolean = false) {
  showFilter.value = false;
  if (refillRunning) {
    return;
  }
  refillRunning = true;
  try {
    console.log("reloadData()");
    if (props.query == null || props.query === "") {
      showLoading.value = false;
      header = { bezeichnung: ";(" };
      return;
    }
    // showSchedule.value = false;
    showLoading.value = true;
    const vecStamm: any[] = [];
    debugger;
    const data = await AUApi.Current.getQuery(
      // Query
      props.query,
      // Done function
      undefined,
      queryAIC,
      zeitbereich,
      0,
      false,
      false,
      "0",
      props.formular?.aic,
      vecStamm
    );
    if (data) {
      await dataLoaded(data, skipRemount);
    }
  } catch (e: any) {
    console.error("mounterror: " + e, e?.stack);
  }
}
async function dataLoaded(data: AxiosResponse<QueryResponse>, skipRemount: boolean = false) {
  // if (props.dienstplan) {
  //   filterKey.value += 1;
  // }
  queryData.value = data.data;
  await fillUpEvents(data, skipRemount);
}
async function fillUpEvents(data: AxiosResponse<QueryResponse>, skipRemount: boolean = false) {
  try {
    if (data.data.error !== undefined) {
      AUApi.Current.onfail(data.data.error);
      console.log(data.data.error);
      // $emit("back");
      alert("ACHTUNG: " + data.data.error);
      return;
    }
    header = data.data.header;
    // rowdata = data.data.data;
    allColumns = [...data.data.columns];
    // nur wenn es auch wirklich einen hinterlegten Filter gibt, darf ShowFilter aktiviert werden!
    const filterColumnsLocal: FilteredDataColumn[] = (filterColumns =
      allColumns.filter(
        (c: any) =>
          c.meta.filterOK === true && !c.meta.invisible && !c.meta.banned
      ) as FilteredDataColumn[]);
    if (!filterColumnsLocal || filterColumnsLocal.length === 0) {
      showFilter.value = false;
    } else {
      showFilter.value = true;
    }
    if (header.edit === false) {
      readOnlyMutable.value = true;
    }
    // nach einer Änderung muss ich neu laden... wie mach ich das mit dem FILTER????
    if (filteredData?.length > 0) {
      // nicht mehr neu füllen!
      rowdata = [...data.data.data];
    } else {
      rowdata = [...data.data.data];
      // bei skipRemount übernimmt fillEvents() unten direkt eine neue dataSource - ein
      // Zwischenschritt auf leer würde hier unnötig ein kurzes Aus-/Wiedereinblenden
      // aller Termine riskieren ("flackert").
      if (!skipRemount) {
        eventSettings.value.dataSource.length = 0;
      }
    }
    // if (!showFilter || !props.dienstplan) {
    //   await fillEvents();
    // }
    await fillEvents(skipRemount);
  } finally {
    // der gemerkte MA soll wieder gesetzt werden
    if (
      savedGroupIndex !== undefined ||
      props.gotoAic ||
      goToAicLocal ||
      savedGroupAic != null
    ) {
      if (goToAicLocal) {
        const group: any = employee.findIndex((obj: any) => {
          return obj.GroupID === goToAicLocal;
        });
        if (group >= 0) {
          savedGroupIndex = group;
        }
        // goToAicLocal ist ein einmaliges "nach dem Schließen des Dialogs zu diesem MA
        // zurückkehren" - ohne Reset würde es JEDEN künftigen Reload (zB einen einfachen
        // Zeitraumwechsel) hijacken und immer wieder auf diesen MA zurückspringen, statt beim
        // MA zu bleiben, den man inzwischen selbst ausgewählt hat (savedGroupIndex aus
        // reloadNewZeitbereich() spiegelt die aktuelle Auswahl bereits korrekt wider).
        goToAicLocal = 0;
      } else if (props.gotoAic) {
        const group: any = employee.findIndex((obj: any) => {
          return obj.GroupID === props.gotoAic;
        });
        if (group >= 0) {
          savedGroupIndex = group;
        }
      } else if (savedGroupAic != null) {
        // Normalfall (einfacher Zeitraumwechsel ohne gotoAic): Index über die GroupID neu
        // in der (ggf. anders sortierten/zusammengesetzten) neuen employee-Liste suchen,
        // statt den alten Index blind wiederzuverwenden.
        const group: any = employee.findIndex((obj: any) => {
          return obj.GroupID === savedGroupAic;
        });
        if (group >= 0) {
          savedGroupIndex = group;
        }
      }
    }
    nextTick(() => {
      const delay =
        props.dienstplan && activeView.value === "TimelineWeek" ? 120 : 0;
      setTimeout(() => {
        const scheduleDok = document.querySelector(".e-schedule");
        if (scheduleDok) {
          const scheduleObj2 = (scheduleDok as any).ej2_instances?.[0];
          const ej2Instance = scheduleObj2?.ej2Instances ?? scheduleObj2;
          if (ej2Instance && ej2Instance.uiStateValues) {
            currentGroupIndex = ej2Instance.uiStateValues.groupIndex;
            if (
              savedGroupIndex !== undefined &&
              savedGroupIndex !== null &&
              savedGroupIndex !== currentGroupIndex
            ) {
              // Im Mobile-Modus (isGroupAdaptive) zeigt der Schedule immer nur EINEN MA als
              // eigenes Panel - ein direktes Setzen von uiStateValues.groupIndex ändert zwar
              // den Zustand, löst aber (anders als die öffentliche API) kein Re-Rendering des
              // Resource-Headers/Panels aus. Der Header blieb dadurch in einem
              // Zwischenzustand hängen (graue Fläche, Name unsichtbar).
              if (
                ej2Instance.uiStateValues.isGroupAdaptive &&
                typeof ej2Instance.selectResourceByIndex === "function"
              ) {
                ej2Instance.selectResourceByIndex(savedGroupIndex);
              } else {
                ej2Instance.uiStateValues.groupIndex = savedGroupIndex;
              }
              // gleiches Problem wie beim Resource-Header oben: der Index-Wechsel allein
              // lässt Syncfusion die Termine der jetzt aktiven Resource nicht neu zeichnen -
              // beim Ersteinstieg mit gotoAic (zB direkt auf MA 6 positioniert) blieb der
              // Schedule dadurch leer, bis man den Zeitraum wechselte.
              refreshScheduleDataSourceWithPending();
            }
            if (
              !suppressAutoResourceScroll.value &&
              savedGroupIndex !== undefined &&
              savedGroupIndex !== null &&
              employee[savedGroupIndex]?.GroupID
            ) {
              ej2Instance.scrollToResource(
                employee[savedGroupIndex].GroupID,
                "Employee"
              );
            }
          }
        }
        // einmaliges Wiederherstellen fürs jeweils AUSLÖSENDE Reload - sonst würde jeder
        // spätere, damit unabhängige reload (Speichern, Refresh, Polling ...) den MA erneut
        // hierher zurückspringen lassen, obwohl der Nutzer inzwischen (zB per Swipe) einen
        // anderen MA gewählt hat.
        savedGroupIndex = undefined;
        savedGroupAic = null;
      }, delay);
    });

    // Mobile Dienstplan: Ladeanzeige bleibt bis onDataBound() (echtes Render-Ende) an, siehe
    // dortiger Kommentar - hier also NICHT vorzeitig ausschalten.
    if (!(props.dienstplan && Constants.isMobile)) {
      showLoading.value = false;
    }
    refillRunning = false;
    initialized.value = true;
    emit("loaded", true);

    if (pendingRefreshAfterReload) {
      pendingRefreshAfterReload = false;
      nextTick(() => refreshScheduleData());
    }
  }
}
async function fillEvents(skipRemount: boolean = false) {
  let startDate: any = null;
  if (rowdata.length > 0) {
    for (let i = 0; i < rowdata.length; i++) {
      try {
        const event = rowdata[i];
        const isReise = getKZValue(event, "reise", allColumns);
        // event.fields.recurrenceID = event.aic_Bew_pool;
        event.startDate = event.StartTime = getKZValue(
          event,
          "von",
          allColumns
        );
        if (!startDate) {
          startDate = new Date(event.StartTime);
        }
        event.EndTime = getKZValue(event, "bis", allColumns);
        if (!event.EndTime) {
          event.EndTime = event.startDate;
        }

        event.IsAllDay = false;
        event.IsReadonly = false;
        let allDay = getKZValue(event, "isAllDay", allColumns);
        const checkBewilligtNr = getKZValue(event, "Antrag", allColumns);
        const checkBewilligt = getKZValueDisplay(event, "Antrag", allColumns);
        const existStellvertreter = getKZValueDisplay(
          event,
          "location",
          allColumns
        ); // = Stellvertreter
        const checkStatusStellvertreterNr = getKZValue(
          event,
          "AntragSV",
          allColumns
        );
        const checkStatusStellvertreter = getKZValueDisplay(
          event,
          "AntragSV",
          allColumns
        );
        bewilligung = getKZValue(event, "zuBewilligen", allColumns);
        event.GroupID = getKZValue(event, "id", allColumns);
        // = AIC Mitarbeiter für Gruppierung zusammenfinden..??
        event.id = resolveGroupResourceId(
          getKZValue(event, "groupID", allColumns)
        );
        // Gruppierung nach Abteilung
        event.idField = event.GroupID;
        event.name = getKZValueDisplay(event, "name", allColumns);
        event.Status = getKZValue(event, "zuBewilligen", allColumns);

        Subject = getKZValueDisplay(event, "subject", allColumns); // = Tagesmuster bei Dienstplan sonst Zeitart
        event.abwesenheit = true;
        if (props.dienstplan) {
          event.abwesenheit = false;
          if (!Subject) {
            Subject = getKZValueDisplay(event, "subject1", allColumns); // = Zeitart
            event.abwesenheit = true;
          } else {
            const showBezeichnung = getKZTitle(
              event,
              "showBezeichnung",
              allColumns
            );
            if (!showBezeichnung && props.dienstplan) {
              if (!event.startDate) {
                let kennzeichen = getKZValueDisplay(
                  event,
                  "Kennzeichen",
                  allColumns
                );
                if (!kennzeichen) {
                  kennzeichen = "freier Tag";
                }
                allDay = true;
                Subject = kennzeichen;
                event.startDate = event.StartTime = getKennungValue(
                  event,
                  "VALIDITY",
                  allColumns
                );
                event.EndTime = event.startDate;
              } else {
                const beginn = new Date(event.startDate);
                const hours = beginn.toLocaleTimeString().substring(0, 5);
                const ende = new Date(event.EndTime);
                const endeString = ende.toLocaleTimeString().substring(0, 5);
                Subject = hours + " - " + endeString;
              }
              // Subject = " ";}
            }
          }
          // beim Dienstplan sollen die Tagesmuster nach open/core/close - gezählt und in der Gruppierung angezeigt werden können
          const checkCount = getKZTitle(event, "count1", allColumns);
          if (checkCount) {
            event.count1 = getKZValue(event, "count1", allColumns);
            event.count2 = getKZValue(event, "count2", allColumns);
            event.count3 = getKZValue(event, "count3", allColumns);
          }
        }
        event.IcsSubject = Subject;
        let abgelehnt = false;
        if (bewilligung && event.abwesenheit) {
          if (checkBewilligtNr === 1 || checkBewilligtNr === true) {
            htmText = "✓";
            event.bewilligt = true;
            if (!props.readBewilligt) {
              event.IsReadonly = true;
            }
          } else if (checkBewilligtNr === 2) {
            htmText = "x";
            abgelehnt = true;
          } else if (checkBewilligtNr === 0 || checkBewilligtNr === false) {
            offen.value = true;
            htmText = "■";
          }
        } else if (checkBewilligtNr && event.abwesenheit) {
          if (checkBewilligtNr === 1) {
            event.bewilligt = true;
            htmText = "✓";
          } else if (checkBewilligtNr === 0) {
            htmText = "offen";
          } else if (checkBewilligtNr === 2) {
            htmText = "x";
          }
        } else if (isReise) {
          const status = getKZValueDisplay(event, "status", allColumns);
          htmText = status;
          event.IsReadonly = getKZValue(event, "readOnly", allColumns);
          const end = new Date(event.EndTime);
          event.EndTime = end;
          if (employee.length === 0) {
            // wenn es die pers. Ansicht ist dann kommt das Ende Datum nicht mit 1 Tag dazu
            // da die Abfarge für die Vuetify Kalender aufgebaut wurde und dort brauch ich es nicht!
            event.EndTime = end.addDays(1).toISOStringWithTZ();
          }
        } else {
          htmText = "";
        }
        if (existStellvertreter && event.abwesenheit) {
          if (checkStatusStellvertreterNr === 1) {
            htmText = htmText + "│" + "✓";
          } else if (checkStatusStellvertreterNr === 2) {
            htmText = htmText + "│" + "x";
          } else if (checkStatusStellvertreterNr === 0) {
            htmText = htmText + "│" + "■";
          } else if (checkStatusStellvertreterNr) {
            htmText = htmText + "│" + "✓";
          }
        }
        if (allDay === 1 || allDay === true) {
          event.IsAllDay = true;
          event.isBlock = true;
        }
        event.editorComponent = props.editorComponent;
        // event.abwesenheit = false;
        event.IcsSubject = Subject;
        const abwText = getKZValue(event, "abwText", allColumns);
        // bei verschleierten Zeitarten soll "abwesend" stehen
        if (abwText) {
          const showText = getKZTitle(event, "abwText", allColumns);
          event.Subject = htmText + " " + showText;
        } else if (htmText) {
          // event.Subject = htmText + " " + Subject;
          if (isReise) {
            // bei der Reise soll am handy beim VG Betrag und Status angezeigt werden - keine Bezeichnung
            event.Subject = Subject;
            if (Constants.isMobile && props.showGroup) {
              event.Location = htmText;
            } else {
              event.Description = htmText;
            }
          } else {
            event.Subject = htmText + " " + Subject;
          }
        } else {
          event.Subject = Subject;
        }
        if (!isReise) {
          event.description = event.Description = getKZValueDisplay(
            event,
            "description",
            allColumns
          );
          event.LocationText = getKZTitle(event, "location", allColumns);
          event.Location = getKZValueDisplay(event, "location", allColumns);
        }
        event.Description2 = getKZValueDisplay(event, "VG", allColumns);
        if (isReise && props.showGroup) {
          if (Constants.isMobile) {
            event.Subject = getKZValueDisplay(event, "location", allColumns);
          } else {
            event.Location = getKZValueDisplay(event, "location", allColumns);
          }
        } else if (isReise && !props.showGroup) {
          event.Location = getKZValueDisplay(event, "memo", allColumns);
        }
        event.text = getKZValueDisplay(event, "text", allColumns);
        event.textText = getKZTitle(event, "text", allColumns);
        const eventreadOnly = getKZValue(event, "isReadOnly", allColumns);
        if (eventreadOnly === true) {
          event.IsReadonly = true;
        }
        const edit = getKZValue(event, "edit", allColumns);
        if (edit === false) {
          event.IsReadonly = true;
        }
        event.exportICS = getKZValue(event, "export", allColumns);
        // ob die Zeitart exportiert werden soll oder nicht"
        event.IsBlock = getKZValue(event, "isBlock", allColumns);
        if (event.IsBlock) {
          const bis = new Date(event.EndTime);
          event.EndTime = bis.toISOStringWithTZ();
          const text = getKZTitle(event, "isBlock", allColumns);
          event.Subject = text;
        } else if (event.abwesenheit && !event.IsAllDay) {
          // es soll die BEGINN Uhrzeit angezeigt werden wenn es keine GanztagesPlanung ist
          const vonString = new Date(event.StartTime).toLocaleTimeString(
            "de-DE",
            {
              hour: "2-digit",
              minute: "2-digit",
            }
          );
          event.Subject = vonString + " " + event.Subject;
        }
        if (event.StartTime < sperrDate.value) {
          event.IsReadonly = true;
        }

        event.Spanned = false;
        const Spanned = getKZValue(event, "Spanned", allColumns);
        if (Spanned === true) {
          event.Spanned = true;
        }
        // event.Location = getKZValueDisplay(event, "location", allColumns);
        if (props.dienstplan) {
          event.dienstplan = true;
          event.text = event.LocationText + ": " + event.Location;
        }
      } catch (e) {
        console.error(e);
      }
    }
  } else {
    // gibt es keine Daten kommt ein error
    // wenn es kein StartDate gibt bei gruppierten Daten
  }
  // 1) tooltip setzen
  if (rowdata.length > 0) {
    if (skipRemount) {
      // Nur die Daten aktualisieren, bestehendes eventSettings-Objekt (fields/allowDeleting/
      // enableTooltip) per Spread wiederverwenden statt komplett neu aufzubauen - genau wie
      // refreshScheduleDataSourceWithPending() (usePendingDienstplanSaves.ts). Eine komplett
      // neue eventSettings-Objektstruktur (auch mit inhaltlich gleichem "fields") lässt
      // Syncfusion offenbar alle Termine einmal aus- und wieder einblenden ("flackert").
      eventSettings.value = {
        ...eventSettings.value,
        dataSource: getScheduleDataSource(),
      };
    } else if (props.currentView !== "Year" && !props.abteilung) {
      if (props.dienstplan) {
        eventSettings.value = {
          dataSource: getScheduleDataSource(),
          allowDeleting: allowDeleting.value,
          enableTooltip: true,
          fields: {
            location: {
              title: "",
              name: "location",
              default: "",
            },
            description: { title: "", name: "description", default: "" },
          },
        };
      } else {
        eventSettings.value = {
          dataSource: getScheduleDataSource(),
          allowDeleting: allowDeleting.value,
          enableTooltip: true,
          fields: {
            location: {
              title: "Stellvertreter",
              name: "Stellvertreter",
              default: "",
            },
            description: { title: "Beschreibung", name: "Beschreibung" },
          },
        };
      }
    } else if (props.currentView === "Year" || props.abteilung) {
      eventSettings.value = {
        dataSource: getScheduleDataSource(),
        enableTooltip: false,
        allowDeleting: allowDeleting.value,
        fields: {
          location: {
            title: "Stellvertreter",
            name: "Stellvertreter",
            default: "",
          },
          description: { title: "Beschreibung", name: "Beschreibung" },
        },
      };
    }
  } else {
    // rowdata leer: eventSettings MUSS trotzdem auf eine leere dataSource zurückgesetzt
    // werden. Sonst bleibt hier eine veraltete dataSource (evtl. mit GroupIDs von zuvor
    // geladenen Mitarbeitern) stehen, während "2) Group Setzen" unten UND der setCompKey-
    // Remount weiter unten trotzdem unbedingt (unabhängig von rowdata.length) laufen - die
    // neu gemountete <ejs-schedule> bekommt dann eine leere group/resources-Konfiguration
    // kombiniert mit einer nicht dazu passenden alten dataSource. Das hat intern in
    // Syncfusion (Month-View Scroll-Neuberechnung, Month.onScrollUIUpdate) zu einem
    // TypeError auf ein nicht mehr vorhandenes DOM-Element geführt, wodurch der interne
    // Syncfusion-Spinner nie mehr ausgeblendet wurde ("Ladeanzeige bleibt hängen").
    eventSettings.value = {
      ...eventSettings.value,
      dataSource: [],
    };
  }
  // 2) Group Setzen
  // group ist ein einfaches (nicht-reaktives) let, das aber bei jedem Re-Render (zB durch
  // die eventSettings-Neuzuweisung unten) frisch ausgewertet und als neue Objekt-Referenz an
  // :group="group" übergeben wird. Syncfusion behandelt eine neue group-Referenz als
  // "Gruppierung geändert" und baut die komplette Resource-Ansicht intern neu auf - das
  // verursacht ein sichtbares Aus-/Wiedereinblenden, auch ohne Vue-seitigen Remount
  // (setCompKey). skipRemount heißt "an der Struktur hat sich nichts geändert, nur die
  // Daten wurden aktualisiert" - group bleibt in dem Fall unverändert (gleiche Referenz).
  if (!skipRemount) {
    // props.group2/props.queryMitarbeiter sind statische Props - sie sagen nichts darüber
    // aus, ob DIESER konkrete Zeitraum-Fetch tatsächlich Ressourcen-Daten geliefert hat. Wird
    // hier trotzdem "Employee"/"GroupData" gruppiert, während employee/groupData für diesen
    // Zeitraum leer sind, bekommt Syncfusion eine Gruppierung ohne dazugehörige Resource-
    // Zeilen im DOM - das hat in der Month-Ansicht zu einem TypeError beim internen Scroll-
    // Dimensions-Update geführt (Month.onScrollUIUpdate: null.offsetHeight), wonach der
    // Syncfusion-eigene Ladespinner nie mehr ausgeblendet wurde. Deshalb hier immer an den
    // tatsächlich vorhandenen Daten (groupData/employee) ausrichten, nicht an der Prop-Config.
    if (props.group2 && groupData && groupData.length > 0) {
      group = {
        enableCompactView: true,
        resources: ["GroupData", "Employee"],
      };
    } else if (employee && employee.length > 0) {
      group = {
        enableCompactView: true,
        resources: ["Employee"],
      };
    } else {
      group = {
        enableCompactView: true,
        resources: [],
      };
    }
  }
  console.log("showSchedule, true, zeile 1219, fillEvents" + "MA wird geladen");
  // Mobile Dienstplan: Ladeanzeige bleibt bis onDataBound() (echtes Render-Ende) an, siehe
  // dortiger Kommentar - hier also NICHT vorzeitig ausschalten.
  if (!(props.dienstplan && Constants.isMobile)) {
    showLoading.value = false;
  }
  showSchedule.value = true;
  if (!skipRemount) {
    setCompKey.value += +1;
  }
  emit("loaded", true);
}
function isColorDark(color: string): boolean {
  let r: number;
  let g: number;
  let b: number;

  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return false;
    }
  } else if (color.startsWith("rgb")) {
    const rgbValues = color.match(/\d+/g);
    if (rgbValues && rgbValues.length >= 3) {
      r = parseInt(rgbValues[0], 10);
      g = parseInt(rgbValues[1], 10);
      b = parseInt(rgbValues[2], 10);
    } else {
      return false;
    }
  } else {
    return false; // unbekanntes Format
  }

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128;
}
function onEventRendered(args: any) {
  const event = args.data;
  if (props.dienstplan && args.element && !event.isLocalPendingDienstplan) {
    args.element.classList.add("dienstplan-existing-event");
  }
  if (event.isLocalPendingDienstplan && args.element) {
    args.element.classList.add("dienstplan-local-pending-event");
    if (event.pendingDienstplanSaveId) {
      args.element.dataset.pendingDienstplanSaveId = String(event.pendingDienstplanSaveId);
    }
    if (
      pendingDienstplanFocusEvent.value &&
      ((event.pendingDienstplanSaveId &&
        event.pendingDienstplanSaveId === pendingDienstplanFocusEvent.value.pendingDienstplanSaveId) ||
        (event.GroupID === pendingDienstplanFocusEvent.value.GroupID &&
          new Date(event.StartTime).getTime() ===
            new Date(pendingDienstplanFocusEvent.value.StartTime).getTime()))
    ) {
      args.element.classList.add("dienstplan-local-pending-event-focus");
    }
  }
  if ((!event.IsBlock || props.dienstplan) && !event.isLocalPendingDienstplan) {
    // wenn geblockt - dann keine Farbe
    // 1.12.2025 Farbe immer intern setzen weil im ALL anderen Farbcodes verewendet werden
    let categoryColor = getKennungValue(event, "FARBE", allColumns);
    if (categoryColor === "" || !categoryColor) {
      categoryColor = getKennungValue(event, "Farbe1", allColumns);
    }
    if (props.useCellTemplate && !props.dienstplan) {
      const checkBewilligt = getKZValue(args.data, "Antrag", allColumns);
      const abgelehnt = getKZValue(args.data, "abgelehnt", allColumns);
      if (abgelehnt || checkBewilligt === 2) {
        categoryColor = "#B00020"; // rot
      } else if (checkBewilligt) {
        categoryColor = "#4CAF50"; // grün
      } else if (checkBewilligt === 0) {
        categoryColor = "#FB8C00";
      } else if (!props.useCellTemplate) {
        categoryColor = "#424242";
      }
    }
    if (!categoryColor || categoryColor === "undefined") {
      categoryColor = "#424242";
    }
    // wenn verschleiert dann grau
    const verschleiern = getKZValue(event, "abwText", allColumns);
    if (!args.element || !categoryColor) {
      return;
    }
    if (verschleiern) {
      args.element.style.backgroundColor = "grey";
    } else {
      args.element.style.backgroundColor = categoryColor;
    }
    if (props.dienstplan && showFilter.value && filteredData.length > 0) {
      // im Dienstplan sollen eventuell gefilterte Daten angezeigt aber GRAU gestellt werden!
      // filteredData.length>0 als Voraussetzung: CommonBaseFilter (das filteredData befüllt)
      // wird selbst nur gerendert wenn v-if="group2 || showText" (Toolbar) zutrifft - bei
      // group2="" (zB Dienstplan mobil, wo group2 aus Ressourcen-Gründen leer sein muss) rendert
      // die Filter-Toolbar nie und filteredData bleibt für immer leer. Ohne diese Bedingung
      // würde dann JEDES Event fälschlich als "nicht im Filter" grau dargestellt.
      const findFilter = filteredData.find(
        (c) => c.aic_Bew_pool === event.aic_Bew_pool
      );

      if (!findFilter) {
        args.element.style.backgroundColor = "rgba(0, 0, 0, 0.08)";
      }
    } else if (props.dienstplan && props.formular?.pers) {
      if (args.data.idField !== AUApi.Current.user.aic) {
        args.element.style.backgroundColor = "lightgrey";
      }
    }
  }
  const bgColor = args.element.style.backgroundColor || "#ffffff";
  if (isColorDark(bgColor)) {
    args.element.style.color = "#ffffff"; // heller Text
  } else {
    args.element.style.color = "#000000"; // dunkler Text
  }
  if (isReadOnly(args.data)) {
    args.element.classList.add("e-past-app");
  }
}
function onRenderCell(args: any) {
  // Tages-Trennlinie in der TimelineWeek - unabhängig von Gruppierung/Dienstplan-Modus,
  // deshalb eigener (vom Badge-Cache unabhängiger) Tracker pro Zeile.
  if (
    activeView.value === "TimelineWeek" &&
    args.date &&
    (args.elementType === "workCells" ||
      args.elementType === "resourceGroupCells" ||
      args.elementType === "dateHeader" ||
      args.element?.classList?.contains("e-time-slots") ||
      args.element?.closest?.(".e-header-row"))
  ) {
    const dayStartLastSeen: Record<string, string> =
      (onRenderCell as any)._dayStartLastSeen || {};
    const date = new Date(args.date);
    const rowKeyBase =
      args.elementType ||
      (args.element?.classList?.contains("e-time-slots") ? "timeSlots" : "headerRow");
    const rowKey = `${rowKeyBase}_${args.groupIndex ?? "single"}`;
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    if (dayStartLastSeen[rowKey] !== dayKey) {
      args.element.classList.add("dienstplan-day-start-cell");
      dayStartLastSeen[rowKey] = dayKey;
      (onRenderCell as any)._dayStartLastSeen = dayStartLastSeen;
    }
  }
  let resourceGroupCellEarliest: Record<string, Date> =
    (onRenderCell as any)._earliestCache || {};
  // Cache zurücksetzen, wenn erste Gruppe und früheste Uhrzeit im sichtbaren Bereich
  if (
    args.elementType === "workCells" &&
    args.element.classList.contains("e-resource-group-cells") &&
    args.date
  ) {
    const date = new Date(args.date);
    if (
      date.getHours() === 0 ||
      date.getHours() === 6 ||
      date.getHours() === 7 ||
      date.getHours() === 8
    ) {
      // typischer Start für Tagesbeginn, ggf. anpassen
      resourceGroupCellEarliest = {};
      (onRenderCell as any)._earliestCache = resourceGroupCellEarliest;
    }
  }
  if (
    args.elementType === "resourceGroupCells" &&
    args.element.classList.contains("e-resource-group-cells") &&
    activeView.value === "TimelineWeek" &&
    props.dienstplan &&
    args.date
  ) {
    const scheduleObj = getSchedule();
    let schedule = scheduleObj.ej2_instances[0];
    const resourceDetails: any = schedule.getResourcesByIndex(args.groupIndex);
    const date = new Date(args.date);
    let groupId: number | undefined = resourceDetails.groupData.id;
    const key = `${groupId}_${date.getFullYear()}_${date.getMonth()}_${date.getDate()}`;
    const prev = resourceGroupCellEarliest[key];
    if (!prev || date < prev) {
      resourceGroupCellEarliest[key] = date;
    }
    (onRenderCell as any)._earliestCache = resourceGroupCellEarliest;
    // Nur Badge anzeigen, wenn dies die früheste Zelle ist
    if (
      resourceGroupCellEarliest[key] &&
      resourceGroupCellEarliest[key].getTime() === date.getTime()
    ) {
      // Prüfe, ob überhaupt count1 im Event existiert
      let hasCount1 = false;
      if (eventSettings.value?.dataSource?.length > 0) {
        for (const ev of eventSettings.value.dataSource) {
          if (typeof ev.count1 === "boolean") {
            hasCount1 = true;
            break;
          }
        }
      }
      if (hasCount1) {
        let count1 = 0,
          count2 = 0,
          count3 = 0;
        eventSettings.value.dataSource.forEach((ev) => {
          const eventGroupId = ev.id;
          const start = ev.StartTime || ev.startDate || ev.von;
          if (!start) return;
          const evDate = new Date(start);
          if (
            eventGroupId == groupId &&
            evDate.getFullYear() === date.getFullYear() &&
            evDate.getMonth() === date.getMonth() &&
            evDate.getDate() === date.getDate()
          ) {
            if (typeof ev.count1 === "boolean") count1 += ev.count1 ? 1 : 0;
            if (typeof ev.count2 === "boolean") count2 += ev.count2 ? 1 : 0;
            if (typeof ev.count3 === "boolean") count3 += ev.count3 ? 1 : 0;
          }
        });
        if (count1 > 0 || count2 > 0 || count3 > 0) {
          args.element.style.position = "relative";
          const badge = document.createElement("div");
          badge.className = "event-count-badge";
          badge.style.cssText =
            "position:absolute;top:2px;right:2px;padding:2px 8px;font-size:13px;z-index:10;box-shadow:0 1px 2px rgba(0,0,0,0.08);font-weight:bold;display:flex;gap:6px;align-items:center;";
          const span = document.createElement("span");
          span.innerText = `${count1}/${count2}/${count3}`;
          span.style.background = "#43a047";
          badge.appendChild(span);
          args.element.appendChild(badge);
        }
      } else {
        // Standardzählung: Wenn count ein Boolean ist, zähle die true-Events
        let count = 0;
        if (eventSettings.value?.dataSource?.length > 0) {
          count = eventSettings.value.dataSource.filter((ev) => {
            const eventGroupId = ev.id;
            const start = ev.StartTime || ev.startDate || ev.von;
            if (!start) {
              return false;
            }
            const evDate = new Date(start);
            const match =
              eventGroupId == groupId &&
              evDate.getFullYear() === date.getFullYear() &&
              evDate.getMonth() === date.getMonth() &&
              evDate.getDate() === date.getDate();
            // Wenn count explizit Boolean ist, nur true zählen
            if (match && typeof ev.count === "boolean") {
              return ev.count === true;
            }
            return match;
          }).length;
        }
        if (count > 0) {
          args.element.style.position = "relative";
          const badge = document.createElement("div");
          badge.className = "event-count-badge";
          badge.style.cssText =
            "position:absolute;top:2px;right:2px;background:red;color:#fff;border-radius:3px;padding:2px 8px;font-size:13px;z-index:10;box-shadow:0 1px 2px rgba(0,0,0,0.08);font-weight:bold;";
          badge.innerText = count + "";
          args.element.appendChild(badge);
        }
      }
    }
  }

  try {
    if (typeof args.groupIndex !== "undefined" && args.element) {
      const bg = args.groupIndex % 2 === 0 ? "#ffffff" : "#e3f2fd";
      args.element.classList.toggle("dienstplan-row-even", args.groupIndex % 2 === 0);
      args.element.classList.toggle("dienstplan-row-odd", args.groupIndex % 2 !== 0);
      if (props.dienstplan && args.elementType === "workCells") {
        args.element.style.setProperty("background", bg, "important");
        args.element.style.setProperty("background-color", bg, "important");
      }
      // nur setzen, wenn nicht bereits von spezieller Logik überschrieben
      if (
        !args.element.style.backgroundColor ||
        args.element.style.backgroundColor === ""
      ) {
        args.element.style.backgroundColor = bg;
      } else {
        // falls schon gesetzt (z.B. Wochenende) -> kombiniere nicht, lasse bestehen
      }
    }
  } catch (e) {
    console.error("onRenderCell row background error", e);
  }
  if (
    (activeView.value === "TimelineWeek" &&
      args.elementType === "dateHeader" &&
      props.dienstplan) ||
    (args.element.classList.contains("e-time-slots") && props.dienstplan)
  ) {
    if (args.date.getDate() % 2 !== 0) {
      args.element.style.backgroundColor = "lightgrey";
    }
  }
  if (args.elementType === "workCells") {
    const groupIndex = args.groupIndex;
    if (groupIndex % 2 === 0) {
      if (activeView.value === "WorkWeek" || activeView.value === "Week") {
        args.element.classList.add("custom-first-group");
      } else {
        args.element.style.backgroundColor = "#ffffff";
      }
    } else {
      if (activeView.value === "WorkWeek" || activeView.value === "Week") {
        args.element.classList.add("custom-second-group");
      } else {
        args.element.style.backgroundColor = "#e3f2fd";
      }
    }
  }
  if (
    props.currentView === "TimelineMonth" &&
    (args.elementType === "workCells" || args.elementType === "monthCells")
  ) {
    const weekEnds = [0, 6];
    if (weekEnds.indexOf(args.date.getDay()) >= 0) {
      const ele = createElement("div", {
        className: "weekend",
      });
      args.element.appendChild(ele);
    }
  }
  if (args.elementType === "monthCells" && props.currentView === "Month") {
    const ele = document.createElement("span");
    ele.className = "templatewrap";
    ele.style.cssText = "color: red;padding-left:5px;";
    ele.innerHTML = getFeiertag(args.date);
    if (Constants.isMobile) {
      ele.innerHTML = ele.innerHTML.slice(0, 5);
    }

    if (ele.innerHTML) {
      const container = args.element as HTMLElement;
      if (container.children.length > 0) {
        container.children[0].appendChild(ele);
      } else {
        container.appendChild(ele);
      }
    }
  } else if (args.elementType === "dateHeader") {
    if (
      props.currentView === "TimelineMonth" ||
      activeView.value === "TimelineWeek" ||
      activeView.value === "TimelineDay" ||
      activeView.value === "Week" ||
      activeView.value === "WorkWeek"
    ) {
      const ele = document.createElement("span");
      ele.className = "templatewrap";
      const Feiertag = getFeiertag(args.date);
      if (Feiertag) {
        ele.style.cssText = "color: red;";
        if (activeView.value === "WorkWeek" || activeView.value === "Week") {
          args.element.style.backgroundColor = "red";
        }
      } else {
        ele.style.cssText = "color: black;";
      }
      if (
        props.currentView === "TimelineMonth" &&
        activeView.value !== "TimelineWeek"
      ) {
        const days = getWeekdayNamesShort();
        const day = args.date;
        const weekday = days[day.getDay()];
        if (Feiertag) {
          const FeiertagText = Feiertag.slice(0, 5);
          ele.innerHTML = FeiertagText + " " + weekday + ".";
        } else {
          ele.innerHTML = weekday + ".";
        }

        if (ele.innerHTML) {
          const container = args.element as HTMLElement;
          container.insertBefore(ele, container.children[0]);
        }
      } else if (
        activeView.value === "TimelineWeek" ||
        activeView.value === "TimelineDay"
      ) {
        const days = getWeekdayNamesShort();
        const day = args.date;
        const weekday = days[day.getDay()];
        if (Feiertag) {
          ele.innerHTML = " (" + Feiertag + ")";
        }
        if (ele.innerHTML) {
          const container = args.element as HTMLElement;
          container.appendChild(ele);
        }
      }
    }
  }
  if (
    args.date &&
    args.element &&
    props.dienstplan &&
    ["TimelineDay", "TimelineWeek", "Week", "WorkWeek"].includes(activeView.value)
  ) {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    args.element.classList.add("e-" + days[args.date.getDay()]);
  }

  if (args.elementType === "resourceHeader") {
    const scheduleObj = getSchedule();
    let schedule = scheduleObj.ej2_instances[0];
    if (schedule) {
      const resourceDetails: any = schedule.getResourcesByIndex(
        args.groupIndex
      );

      const groupIndex = args.groupIndex;
      // if (groupIndex % 2 === 0) {
      //   args.element.style.backgroundColor = "#e4f3f8";
      // } else {
      //   args.element.style.backgroundColor = "#def8da";
      // }
      if (args.element.classList.contains("e-parent-node")) {
        if (resourceDetails.resource.colorField) {
          args.element.style.backgroundColor =
            resourceDetails.resourceData[resourceDetails.resource.colorField];
        } else if (groupIndex % 2 === 0) {
          args.element.style.backgroundColor = "#ffffff";
        } else {
          args.element.style.backgroundColor = "#e3f2fd";
        }
      } else {
        if (groupIndex % 2 === 0) {
          args.element.style.backgroundColor = "#ffffff";
        } else {
          args.element.style.backgroundColor = "#e3f2fd";
        }
      }
    }
  }
}
function cleanupScheduleIndicators(delay = 0) {
  window.setTimeout(() => {
    document
      .querySelectorAll(".e-indicator.e-icons.e-up-icon")
      .forEach((el) => el.remove());
  }, delay);
}
function navigating(args: any) {
  showLoading.value = true;
  console.log("Navigating event ausgelöst", args);
  // Prüfe ob nur View wechselt, aber Datum gleich bleibt
  const currentSelected: Date = args.currentDate;
  const previousSelected: Date = args.previousDate;

  let currentViewLocal = activeView.value;
  if (args.currentView) {
    currentViewLocal = args.currentView;
  }

  // Wenn Datum gleich und nur View wechselt → keine Neuladen
  const dateUnchanged =
    currentSelected &&
    previousSelected &&
    currentSelected.toISOStringWithTZ() ===
      previousSelected.toISOStringWithTZ();
  const onlyViewChange =
    args.action === "view" ||
    (dateUnchanged &&
      args.currentView &&
      args.currentView !== activeView.value);
  if (onlyViewChange && dateUnchanged) {
    console.log("nur View wechselt, kein Reload nötig");
    // View-Typ speichern (für Dienstplan)
    if (props.dienstplan && currentViewLocal) {
      window.localStorage.setItem("activeAnzeigeDPVG", currentViewLocal);
    }
    // activeView anpassen
    activeView.value = currentViewLocal;

    // TimeScale / workHours anpassen
    if (currentViewLocal === "Week" && props.dienstplan) {
      timeScale.value = { interval: 60, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
    if (currentViewLocal === "WorkWeek" && props.dienstplan) {
      timeScale.value = { interval: 60, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
    if (currentViewLocal === "TimelineWeek" && props.dienstplan) {
      timeScale.value = { interval: 120, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
    if (
      args.action !== "view" &&
      (args.action !== "date" ||
        (args.action === "date" && activeView.value === "TimelineYear") ||
        (args.action === "date" && activeView.value === "Month"))
    ) {
      return;
    }
  }

  if (props.dienstplan && currentViewLocal) {
    window.localStorage.setItem("activeAnzeigeDPVG", currentViewLocal);
  }
  debugger;
  setSelectedDate(args.currentDate ?? selectedDate.value);

  // Zeitbereich für die neue View ermitteln
  if (
    currentViewLocal === "Day" ||
    currentViewLocal === "TimelineDay" ||
    currentViewLocal === "Week" ||
    currentViewLocal === "TimelineWeek" ||
    currentViewLocal === "WorkWeek"
  ) {
    const startDate = currentSelected;
    setSelectedDate(startDate);
    activeView.value = currentViewLocal;
    if (currentViewLocal === "Week" && props.dienstplan) {
      timeScale.value = { interval: 60, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
    if (currentViewLocal === "WorkWeek" && props.dienstplan) {
      timeScale.value = { interval: 60, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
    if (currentViewLocal === "TimelineWeek" && props.dienstplan) {
      timeScale.value = { interval: 120, slotCount: 1 };
      applyTimelineWeekHoursFromEmployee();
    }
  } else if (
    currentViewLocal === "Month" ||
    currentViewLocal === "TimelineMonth"
  ) {
    // In Monatsansichten immer auf den Monatsanfang setzen,
    // damit nicht der aktuelle Tag (z.B. der 17.) hängen bleibt
    const baseDate = currentSelected ?? selectedDate.value;
    if (baseDate) {
      setSelectedDate(new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      ));
    }
    activeView.value = currentViewLocal;
  } else if (
    currentViewLocal === "TimelineYear" ||
    currentViewLocal === "Year"
  ) {
    if (selectedDate.value) {
      const date = selectedDate.value;
      const year = date.getFullYear();
      setSelectedDate(new Date(year, 0, 1));
    }
    activeView.value = currentViewLocal;
  }

  let dt: string | null = null;
  if (selectedDate.value) {
    dt = selectedDate.value.toISOStringWithTZ();
  }
  cleanupScheduleIndicators(50);
  // Nur neu laden wenn sich Datum tatsächlich geändert hat
  // bei TimelineDay - wieder zurück auf Week etc.. muss er immer laden - da die prüfung auf den Tag nicht klappt
  if (
    lastDt !== dt ||
    args.previousView === "TimelineDay" ||
    args.previousView === "TimelineYear" // wenn ich von Jahr auf Monat wechsel - muss auch neu geladen werden - weil die daten aus dem Vormonat mit angezeigt werden sollen (Dezember)
  ) {
    lastDt = dt;
    reloadNewZeitbereich(true);
  } else {
    showLoading.value = false;
  }
}
function isReadOnly(data: any) {
  // getSperrDate();
  if (sperrDate.value) {
    return data.EndTime < sperrDate.value;
  } else {
    return false;
  }
}
function getFeiertag(value: any) {
  const holiday = AUApi.Current.getHoliday(value);

  if (holiday) {
    const text = holiday.bezeichnung;
    return text;
  }

  return "";
}
function getSperrDate() {
  const aic = AUApi.Current.user.aic;
  if (props.query) {
    const t = findSperrDate(props.query, aic, props.vgSicht);
    if (t) {
      sperrDate.value = t.sperrDatum;
      // einen Tag wegrechnen - sonst wird der 1. des Monats gesperrt
      // es gilt da Datum aber Exclusiv!
      sperrDate.value = sperrDate.value.addDays(-1);
      const sperrDateString = sperrDate.value.toISOStringWithTZ();
      return new Date(sperrDateString);
    }
  }
}
function loadImage(avatar: any) {
  if (avatar.name) {
    avatarIcon = AUApi.Current.fullUrl(
      "imageM/" + AUApi.Current.user.id + ":" + +avatar.aic + "_" + avatar.name
    );
  } else {
    avatarIcon = "mdi-account-circle";
  }
}
function onDialogSave() {
  showDialog.value = false;
  showRefresh.value = true;
  // zB nach dem Löschen eines bereits gespeicherten Termins (DialogDienstplanAbwesenheiten
  // -> bewDelete() -> onDelete() -> emit("backDelete")): reloadData(true) aktualisiert nur
  // die Daten (skipRemount), ohne die Schedule-Instanz per :key komplett neu aufzubauen -
  // sonst würde die Ansicht dabei sichtbar ganz nach oben springen, bevor
  // reloadPreservingScroll die Position auf der neuen Instanz wiederherstellt.
  reloadPreservingScroll(() => reloadData(true));
}
function dialogBack(updatedata: any) {
  showDialog.value = false;

  const shouldReload = updatedata === true || props.reload === true;

  if (shouldReload) {
    showRefresh.value = true;
    reloadPreservingScroll(() => reloadData(true));
  } else {
    // kein Reload nötig — Dialog wurde nur geschlossen ohne Änderung
    console.log("Dialog geschlossen ohne Änderung, kein Reload");
  }
}
function onDeletePendingLocal(pendingId: string, abwAicAbwesenheiten?: number) {
  showDialog.value = false;
  if (!pendingId && !abwAicAbwesenheiten) {
    return;
  }
  discardPendingDienstplanSave(pendingId, abwAicAbwesenheiten);
}

function onDelete(aic: any, goBackOnSuccess: boolean = true) {
  showDialog.value = false;
  const bewPool = aic ?? dialog.aic;
  if (!bewPool) {
    showSnackbar("keinen Datensatz übergeben!");
    return;
  }
  const aicAbfAllBez = globalsKennung(Globals.Begriff, "WebPlanungSpeichern");
  if (!aicAbfAllBez) {
    showSnackbar({
      text: "Abfrage nicht gefunden",
      color: "error",
    });
    showLoading.value = false;
    return;
  }
  const aicAbfAll = aicAbfAllBez.aic;
  AUApi.Current.DeleteData(
    header,
    bewPool,
    aicAbfAll,
    zeitbereich,
    false,
    (res: any) => {
      const msgType = res.data.msgType;
      let info = res.data.info;
      const memo = res.data.memo;
      const title = res.data.titel;
      if (memo) {
        info = memo;
      }

      if (info) {
        if (msgType === "Error") {
          showSnackbar({
            text: info,
            color: "error",
          });
        } else if (msgType === "Info") {
          showSnackbar({
            text: info,
            color: "info",
          });
        } else if (msgType === "Warnung") {
          showSnackbar({
            text: info,
            color: "warning",
          });
        }
        showLoading.value = false;
        return msgType;
      }

      showSnackbar(
        globalsBegriffMemo(
          Globals.Begriff,
          "Datensatz_geloescht",
          "Daten wurden gelöscht"
        )
      ); // Geloescht
      if (goBackOnSuccess) {
      }
      deleteRow(bewPool);
    },
    (ex: any) => {
      if (ex && ex.response) {
        let errorMessage = "";
        if (ex.response) {
          errorMessage = ex.response.data.error;
        } else {
          errorMessage = ex;
        }
        emit("alert", errorMessage, "error");
      }
      console.log(ex);
      if (ex !== AUApi.Current.ApiBusyMessage && ex.response) {
        showSnackbar(
          globalsBegriffMemo(
            Globals.Begriff,
            "Berechnung_lauft",
            "Berechnung läuft, bitte warten Sie diese ab."
          )
        );
      }
      deleteRow(bewPool);
    }
  );
}
function deleteRow(aic: number) {
  if (!aic) {
    return;
  }
  // Kein showSchedule-Toggle/setCompKey-Bump (voller Remount) mehr nötig - eventSettings ist
  // reaktiv an :eventSettings gebunden, der EJ2-Vue-Wrapper übernimmt eine Neuzuweisung
  // bereits selbst (setProperties -> dataBind), siehe refreshScheduleDataSourceWithPending()
  // in usePendingDienstplanSaves.ts. Das vermeidet sowohl den Sprung nach oben als auch das
  // kurze Ausblenden/Wiedereinblenden der ganzen Ansicht beim Löschen ("flackert").
  eventSettings.value = {
    ...eventSettings.value,
    dataSource: eventSettings.value.dataSource.filter(
      (row: any) => row.aic_Bew_pool !== aic
    ),
  };
  rowdata = rowdata.filter((row: any) => row.aic_Bew_pool !== aic);
  filteredData = filteredData.filter((row: any) => row.aic_Bew_pool !== aic);
  emit("loaded", true);
}

function checkDateRange(von: Date, bis: Date, aic: number): boolean {
  // bei TRUE darf ich buchen - bei FLASE nicht
  const rows = [...rowdata];
  for (let i = 0; i < rows.length; i++) {
    const eventVon = new Date(rows[i].StartTime);
    const eventBis = new Date(rows[i].EndTime);
    // bis = 1 Tag mehr!
    if (Date.doesRangesIntersect(eventVon, eventBis, von, bis)) {
      // wurde die bestehende Planung geändert, dann darf ich nicht auf FALSE setzen!
      console.log(
        "intersect: " + von.toISOStringWithTZ() + " " + bis.toISOStringWithTZ()
      );
      console.log(
        "           " +
          eventVon.toISOStringWithTZ() +
          " " +
          eventBis.toISOStringWithTZ()
      );

      if (!aic || aic !== rows[i].aic_Bew_pool) {
        showSnackbar({
          text: globalsBegriffMemo(
            Globals.Begriff,
            "Ueberschneidung",
            "Überschneidung!"
          ),
          color: "error",
        });
        return false;
      }
    }
  }
  return true;
}

function checkOneEventPerDay(
  von: Date,
  bis: Date,
  employeeAic: any,
  ownAicBewPool: number,
  isAbwesenheit: boolean = false
): boolean {
  // wie checkDateRange, aber zusätzlich auf den selben Mitarbeiter UND den selben Typ
  // (Dienstplan bzw. Abwesenheit) eingeschränkt - sonst würde bei einer Gruppenansicht
  // jeder andere Mitarbeiter, oder eine Abwesenheit ggü. einem Dienstplan-Eintrag,
  // den Speichervorgang fälschlich blockieren
  const rows = [...rowdata];
  for (let i = 0; i < rows.length; i++) {
    if (String(rows[i].GroupID) !== String(employeeAic)) {
      continue;
    }
    if (!!rows[i].abwesenheit !== isAbwesenheit) {
      continue;
    }
    const eventVon = new Date(rows[i].StartTime);
    const eventBis = new Date(rows[i].EndTime);
    if (Date.doesRangesIntersect(eventVon, eventBis, von, bis)) {
      let pruefAic = rows[i].aic_Bew_pool;
      if (isAbwesenheit) {
          const abwAicPlanung = Number(getKennungValue(rows[i], "WEBPLANUNGBEWBEW", allColumns));
          if (abwAicPlanung) {
            pruefAic = abwAicPlanung;
          }
      }

      if (!ownAicBewPool || ownAicBewPool !== pruefAic) {
        showSnackbar({
          text: globalsBegriffMemo(
            Globals.Begriff,
            "Ueberschneidung",
            "Überschneidung!"
          ),
          color: "error",
        });
        return false;
      }
    }
  }
  if (props.dienstplan) {
    // auch gegen noch nicht gespeicherte lokale Vormerkungen (selber Mitarbeiter, selber Typ)
    // prüfen - der gerade bearbeitete pending Eintrag darf sich dabei nicht selbst blockieren
    for (const entry of pendingDienstplanSaves.value) {
      if (entry.id === dialog.pendingDienstplanSaveId) {
        continue;
      }
      const preview = entry.previewEvent;
      if (
        isAbwesenheit &&
        dialog.abwAicAbwesenheiten &&
        preview?.abwAicAbwesenheiten &&
        Number(preview.abwAicAbwesenheiten) === Number(dialog.abwAicAbwesenheiten)
      ) {
        // selbe Abwesenheits-Bewegung, nur unter einer anderen pending-ID vorgemerkt
        continue;
      }
      if (!preview?.StartTime || !preview?.EndTime) {
        continue;
      }
      if (String(preview.GroupID) !== String(employeeAic)) {
        continue;
      }
      if (!!preview.abwesenheit !== isAbwesenheit) {
        continue;
      }
      const eventVon = new Date(preview.StartTime);
      const eventBis = new Date(preview.EndTime);
      if (Date.doesRangesIntersect(eventVon, eventBis, von, bis)) {
        showSnackbar({
          text: globalsBegriffMemo(
            Globals.Begriff,
            "Ueberschneidung",
            "Überschneidung!"
          ),
          color: "error",
        });
        return false;
      }
    }
  }
  return true;
}
function onPopupOpen(args: {
  data: any;
  date: any;
  type: any;
  element: any;
  cancel: any;
  requestType: string;
  items: any;
}) {
  console.log("onPopupOpen()", args);
  const entry = args.data as any; // weiß nicht wo das herkommt -- CalendarEntry;
  if (args.type === "EditEventInfo") {
    if (Constants.isMobile) {
      if (props.onlyEdit && readOnlyMutable.value) {
        entry.IsReadonly = true;
        args.cancel = true;
      }
    }
  }
  const dtBeginn =
    entry.date ?? args.date ?? entry.StartTime.toISOStringWithTZ();
  // bewilligt Status nur prüfen wenn es ein gespeicherter Satz ist
  // const dtBeginn = args.data.StartTime.toISOStringWithTZ();
  if (entry.aic_Bew_pool || entry.aic_Stamm) {
    const checkBewilligt = getKZValueDisplay(args.data, "Antrag", allColumns);
    const checkBewilligtNr = getKZValue(args.data, "Antrag", allColumns);
    bewilligung = getKZValue(args.data, "zuBewilligen", allColumns);
    bewilligt.value = false;

    if (!props.readBewilligt) {
      if (bewilligung) {
        if (checkBewilligtNr === "1") {
          bewilligt.value = true;
          entry.IsReadonly = true;
        }
      }
    }
    if (sperrDate.value !== null) {
      if (dtBeginn < sperrDate.value) {
        entry.IsReadonly = true;
        args.cancel = true;
        // readBewilligt = false; - Prop darf ich nicht ändern...
        showQuickInfoMutable.value = true;
        showDialog.value = false;
        showSnackbar({
          text: globalsBegriffMemo(
            Globals.Begriff,
            "Periodensperre",
            "Zeitraum gesperrt!"
          ),
          duration: 2000,
          color: "error",
        });
      }
    }
  } else if (!showGroupMutable.value) {
    // (!showGroupMutable && entry.Subject) // dann geht er beim Handy bei NEU trotzdem rein..
    const dateCheck = checkDateRange(
      entry.StartTime,
      entry.EndTime,
      entry.aic_Bew_pool
    );
    if (!dateCheck) {
      console.log("intersect line 921");

      entry.IsReadonly = true;
      args.cancel = true;
      return;
    }
  }
  if (sperrDate.value) {
    // const startSperrDate = new Date(sperrDate.value).addMonths(-60, false);
    if (args.data.StartTime < sperrDate.value) {
      entry.IsReadonly = true;
      args.cancel = true;
      // readBewilligt = false; - Prop darf ich nicht ändern...
      showQuickInfoMutable.value = true;
      showDialog.value = false;
      showSnackbar({
        text: globalsBegriffMemo(
          Globals.Begriff,
          "Periodensperre",
          "Zeitraum gesperrt!"
        ),
        duration: 2000,
        color: "error",
      });
      // return;
    }
  }
  if (props.onlyEdit && !args.cancel) {
    if (args.type === "QuickInfo") {
      // schaltet delete Button in der QuickInfo aus!
      const deleteEle = args.element.querySelector(".e-event-popup .e-delete");
      if (deleteEle) {
        (deleteEle as any).ej2_instances[0].disabled = true;
      }
    }
    if (
      (args.type === "QuickInfo" || args.type === "Editor") &&
      !entry.Subject
    ) {
      entry.IsReadonly = true;
      args.cancel = true;
      // props.readBewilligt = false;
      showQuickInfoMutable.value = true;
      showDialog.value = false;
      return;
    }
  }
  if (!props.readBewilligt) {
    if (
      args.type === "QuickInfo" &&
      (bewilligt || entry.IsReadonly || props.readOnly)
    ) {
      showQuickInfoMutable.value = true;
      entry.IsReadonly = true;
      showDialog.value = false;

      if (entry.Description) {
        const rowDescription = createElement("div", {
          className: "e-description",
        });
        const iconDiv = createElement("div", {
          className: "e-description-icon e-icons",
        });
        const Description = createElement("div", {
          className: "e-description-details",
        });
        Description.innerText = entry.Description;
        rowDescription.appendChild(iconDiv);
        rowDescription.appendChild(Description);
        const popupElement = args.element.querySelector(".e-popup-content");
        popupElement?.appendChild(rowDescription);
      }
      if (entry.Location) {
        const rowLocation = createElement("div", {
          className: "e-description",
        });
        const iconDiv = createElement("div", {
          className: "e-resource-icon e-icons",
        });
        const Stellverterter = createElement("div", {
          className: "e-description-details",
        });
        Stellverterter.innerText = entry.Location;
        rowLocation.appendChild(iconDiv);
        rowLocation.appendChild(Stellverterter);
        const popupElement = args.element.querySelector(".e-popup-content");
        popupElement?.appendChild(rowLocation);
      }
      if (entry.Description2) {
        const rowDes = createElement("div", { className: "e-description" });
        const iconDiv = createElement("div", {
          className: "e-description-icon e-icons",
        });
        const Description2 = createElement("div", {
          className: "e-description-details",
        });
        Description2.innerText = entry.Description2;
        rowDes.appendChild(iconDiv);
        rowDes.appendChild(Description2);
        const popupElement = args.element.querySelector(".e-popup-content");
        popupElement?.appendChild(rowDes);
      }
    }
  }
  if (args.type === "QuickInfo" && !bewilligt && entry.IsReadonly) {
    if (Constants.isMobile) {
      if (props.onlyEdit || readOnlyMutable.value) {
        allowDeleting.value = false;
      }
    }
    if (!Constants.isMobile) {
      console.log("cancel on NOT mobile ");
      args.cancel = showQuickInfoMutable.value; // laut Forum kann man am Handy die Quickinfo NICHT ausschalten!
    }
  } else if (args.type === "Editor" || args.type === "ViewEventInfo") {
    if (!entry.IsReadonly && props.editorComponent) {
      args.cancel = true;
      let startTime = null;
      let endTime = null;
      let IsAllDay = false;
      let insertHint = false;
      // Abwesenheitsplanung steht in 2 Bewegungen!
      // bei einer lokalen Vormerkung (args.data = previewEvent) gibt es keine echte
      // Spalte "WEBPLANUNGBEWBEW" - dort steht der Wert direkt auf entry.abwAicAbwesenheiten
      const abwAicAbwesenheiten =
        Number(getKennungValue(args.data, "WEBPLANUNGBEWBEW", allColumns)) ||
        Number(entry.abwAicAbwesenheiten) ||
        0;
      console.log("[baseSyncSchedule] insertHint Entscheidung", {
        abwesenheit: entry.abwesenheit,
        isLocalPendingDienstplan: entry.isLocalPendingDienstplan,
        aic_Bew_pool: entry.aic_Bew_pool,
        aic_Stamm: entry.aic_Stamm,
        abwAicAbwesenheiten,
      });
      if (!entry.isLocalPendingDienstplan && (entry.aic_Bew_pool || entry.aic_Stamm || abwAicAbwesenheiten)) {
        startTime = getKZValue(args.data, "von", allColumns);
        endTime = getKZValue(args.data, "bis", allColumns);
        if (!startTime) {
          startTime = getKennungValue(args.data, "VALIDITY", allColumns);
        }
        if (!endTime) {
          endTime = startTime;
        }
        IsAllDay = getKZValue(args.data, "isAllDay", allColumns);
        insertHint = false;
      } else {
        insertHint = true;
        if (entry.startTime) {
          startTime = entry.startTime;
        } else if (entry.StartTime) {
          startTime = entry.StartTime;
        }
        if (entry.endTime) {
          endTime = entry.endTime;
        } else if (entry.EndTime) {
          endTime = entry.EndTime;
        }
      }
      const dialogZeitbereich: APIZeitbereich = {
        bereich: "Tag",
        von: new Date(startTime),
        bis: new Date(endTime),
      }; // damit zeitraum stimmt
      dialog.zeitbereich = dialogZeitbereich;
      dialog.IsAllDay = IsAllDay;
      dialog.insertMode = insertHint;
      dialog.abwAicAbwesenheiten = abwAicAbwesenheiten;
      dialog.pendingDienstplanSaveId = entry.isLocalPendingDienstplan
        ? entry.pendingDienstplanSaveId
        : null;
      // beim Öffnen einer lokal vorgemerkten (pending) Buchung die zuletzt eingegebenen
      // Daten (von/bis, Tagesmuster, ...) statt einer Server-Abfrage vorbelegen
      dialog.preloadedData = null;
      if (entry.isLocalPendingDienstplan && dialog.pendingDienstplanSaveId) {
        const pendingEntry = pendingDienstplanSaves.value.find(
          (item) => item.id === dialog.pendingDienstplanSaveId
        );
        if (pendingEntry?.request) {
          dialog.preloadedData = {
            data: {
              header: pendingEntry.request.header,
              columns: pendingEntry.request.columns,
              data: [pendingEntry.request.data],
            },
          };
        }
      }
      if (entry.GroupID && !props.dialogAicStamm) {
        // dialaogAIcStamm = AIC vom EVENT nehmen (Reisekosten)
        dialog.aic = entry.GroupID;
        dialog.groupID = entry.GroupID;
        dialog.groupName = entry.name;
        goToAicLocal = dialog.aic; // AIC merken - um nach dem schliessen wieder am richtigen zu stehen!
      } else if (entry.aic_Stamm) {
        dialog.aic = entry.aic_Stamm;
        goToAicLocal = entry.GroupID;
        // wenn aus dem Dialog noch weiter auf eine Submaske
        // gegangen wird - brauch ich den MA (Zb von den Reisen zu den Buchungen)
        dialog.groupID = entry.GroupID;
        dialog.grouName = entry.name;
      }
      dialog.abwesenheit = entry.abwesenheit;
      if (entry.editorComponent) {
        dialog.editorComponent = entry.editorComponent;
      } else {
        dialog.editorComponent = props.editorComponent;
      }
      showDialog.value = true;
      console.log("showing dialog", dialog, dialogZeitbereich);
      args.cancel = true;
      return;
    }
  }
}
function onChange(args: any) {
  // const schedule = getSchedule();
  checked.value = args;
  persistGroupState(Boolean(args));
  // showAbteilung darf nur aktiviert werden, wenn groupData auch echte Daten hat (siehe
  // fillUpResource) - sonst kann Syncfusion Employee nicht mehr auflösen.
  if (args && groupData.length > 0) {
    showAbteilung.value = true;
  } else {
    showAbteilung.value = false;
  }
}
function changeGroup(change?: boolean) {
  checked.value = true;
  persistGroupState(true);
  const schedule = getSchedule();
  if (groupData.length > 0) {
    showAbteilung.value = true;
  }
  schedule.dataBind();
}
async function filteredDataChanged(filterData: FilteredQueryResponse) {
  if (showFilter.value) {
    filteredData = filterData.data;
    const data = filterData.data;
    if (data && data.length > 0 && allColumns?.length > 0) {
      const colls = allColumns;
      const searchText = filterData.searchText?.toLowerCase();
      const filterEmployee = employeeOriginal.filter((e) => {
        const inData =
          filterData.data.findIndex(
            (d: any) => getKZValue(d, "id", colls) === e.GroupID
          ) >= 0;
        const inText =
          !searchText || (e.Text?.toLowerCase().indexOf(searchText) ?? -1) >= 0;
        // wenn KEIN Text erfasst ist - aber filter ACTIVE gesetzt ist - dann nur die Zeilen MIT Daten!
        if (filterData.filterSet) {
          filterData.filterActive = true;
        }
        if (filterData.filterActive) {
          return inData || (inText && searchText);
        } else {
          return inData || !searchText || (inText && searchText);
        }
      });
      employee = filterData.filterActive ? filterEmployee : employeeOriginal;
    } else {
      if (filterData.noData && !props.dienstplan) {
        showText.value = true;
        showSchedule.value = false;
        employee = [];
        emit("loaded", true);
      }
    }
    // nur die MA filtern! nicht die Daten selber

    if (props.dienstplan) {
      // this.rowdata = filteredData.data;
      rowdata = [...filterData.data];
      //rowdata = [...rowdata];
    } else {
      rowdata = [...filterData.data];
      if (rowdata.length === 0) {
        // showText.value = true;
      }
    }
    eventSettings.value.dataSource = [];
    console.log("fillEvents - nach FILTER setzen");
    await fillEvents();
  }
}

function getDienstplanScheduleElement(): HTMLElement | null {
  return document.querySelector(".dvhDienstplan.e-schedule") as HTMLElement | null;
}

function getTimelineContentWrap(): HTMLElement | null {
  return getDienstplanScheduleElement()?.querySelector(
    ".e-content-wrap"
  ) as HTMLElement | null;
}

function updateFixedTimelineScrollbar(contentWrap = getTimelineContentWrap()) {
  if (!showFixedTimelineScrollbar.value) {
    fixedTimelineScrollLeft.value = 0;
    fixedTimelineScrollMax.value = 0;
    return;
  }

  if (!contentWrap) {
    return;
  }

  // showFixedTimelineScrollbar.value ist an dieser Stelle bereits true (TimelineWeek/Week/
  // WorkWeek im Dienstplan) - overflow erzwingen, damit horizontal überhaupt gescrollt werden kann
  contentWrap.style.overflowX = "auto";
  contentWrap.style.overflowY = "auto";

  const nextMax = Math.max(
    0,
    contentWrap.scrollWidth - contentWrap.clientWidth
  );
  const outerTable = contentWrap.querySelector(".e-schedule-table") as HTMLElement | null;
  const rows = outerTable
    ? Array.from(outerTable.querySelectorAll("tbody > tr")) as HTMLElement[]
    : [];
  console.log("[fixedTimelineScrollbar] Breiten-Vergleich", {
    contentWrapScrollWidth: contentWrap.scrollWidth,
    contentWrapClientWidth: contentWrap.clientWidth,
    nextMax,
    outerTableScrollWidth: outerTable?.scrollWidth,
    outerTableOffsetWidth: outerTable?.offsetWidth,
    rowWidths: rows.map((row, i) => ({
      index: i,
      scrollWidth: row.scrollWidth,
      offsetWidth: row.offsetWidth,
    })),
    contentWrapScrollHeight: contentWrap.scrollHeight,
    contentWrapClientHeight: contentWrap.clientHeight,
    contentWrapOverflowY: getComputedStyle(contentWrap).overflowY,
    outerTableScrollHeight: outerTable?.scrollHeight,
    outerTableOffsetHeight: outerTable?.offsetHeight,
  });
  if (contentWrap.scrollWidth > 0 && contentWrap.clientWidth > 0) {
    fixedTimelineScrollLeft.value = contentWrap.scrollLeft;
    fixedTimelineScrollMax.value = nextMax;
  }
}

function refreshFixedTimelineScrollbar() {
  updateFixedTimelineScrollbar();
  nextTick(() => {
    updateFixedTimelineScrollbar();
    window.setTimeout(() => updateFixedTimelineScrollbar(), 120);
    window.setTimeout(() => updateFixedTimelineScrollbar(), 300);
  });
}

function onFixedTimelineScroll(event: Event) {
  const contentWrap = getTimelineContentWrap();
  const value = Number((event.target as HTMLInputElement).value);
  if (!contentWrap || !Number.isFinite(value)) {
    return;
  }

  contentWrap.scrollLeft = value;
  fixedTimelineScrollLeft.value = value;
}

function onDataBound() {
  if (props.dienstplan && Constants.isMobile) {
    // Ladeanzeige bleibt bis hierher (echtes Render-Ende von Syncfusion) sichtbar, statt
    // schon direkt nach dem Datenladen (fillEvents/fillUpEvents) zu verschwinden - sonst
    // sieht man kurz einen leeren Schedule, bevor Syncfusion (bzw. der obige Workaround)
    // die Termine tatsächlich zeichnet.
    showLoading.value = false;
  }
  // wenn nur Edit - ADD Button wegschalten
  // innerhalb des Suppress-Fensters starten wir bereits als "gerade wiederhergestellt" -
  // deckt einen zweiten, kurz danach folgenden dataBound-Aufruf ab (siehe Kommentar oben
  // bei scrollRestoreSuppressUntil), nicht nur den einen Aufruf, der tatsächlich restauriert.
  let restoredPendingScrollPosition = Date.now() < scrollRestoreSuppressUntil;
  const scheduleDok = document.querySelector(".e-schedule");
  if (scheduleDok) {
    const scheduleObj = (scheduleDok as any).ej2_instances[0];

    let addButton = scheduleObj.element.querySelector(".e-toolbar-item.e-add");

    if (addButton) {
      if (props.onlyEdit) {
        addButton.classList.add("e-hide-add-button");
      } else {
        addButton.classList.remove("e-hide-add-button");
      }
    }
  }

  // Scroll-Position nach reloadPreservingScroll() wiederherstellen - unabhängig von
  // props.dienstplan (zB nach onDialogSave()/dialogBack() in einer normalen, nicht
  // gruppierten Ansicht wie Stellvertreter). Ohne das hier springt Syncfusion nach dem
  // Reload auf die zuletzt fokussierte Resource (scrollToResource in fillUpEvents' finally-
  // Block) statt an der Stelle zu bleiben, an der man vorher war.
  if (pendingScheduleScrollRestore.value) {
    // Zielposition merken - verhindert den sichtbaren Sprung zur Default-Position (oben)
    // und wieder zurück.
    lastRestoredScrollPosition = pendingScheduleScrollRestore.value;
    pendingScheduleScrollRestore.value = null;
    // Fenster öffnen, damit ein kurz danach folgender zweiter dataBound-Aufruf
    // (Ressourcen-Virtualisierung/Settle der neuen Instanz) dieselbe Position erneut
    // anwendet, statt sie zurückspringen zu lassen. Deckungsgleich mit
    // suppressAutoResourceScroll (usePendingDienstplanSaves.ts, 1000ms).
    scrollRestoreSuppressUntil = Date.now() + 1000;
    restoredPendingScrollPosition = true;
  }
  if (scheduleDok && lastRestoredScrollPosition && restoredPendingScrollPosition) {
    const genericContentWrap = scheduleDok.querySelector(
      ".e-content-wrap"
    ) as HTMLElement | null;
    if (genericContentWrap) {
      genericContentWrap.scrollTop = lastRestoredScrollPosition.scrollTop;
      genericContentWrap.scrollLeft = lastRestoredScrollPosition.scrollLeft;
    }
  }

  // Dienstplan: Scroll-Sync zwischen Resource-Panel und Content-Panel erzwingen
  if (props.dienstplan) {
    const scheduleEl = document.querySelector(".dvhDienstplan.e-schedule");
    if (scheduleEl) {
      const contentWrap = scheduleEl.querySelector(
        ".e-content-wrap"
      ) as HTMLElement;
      const dateHeaderWrap = scheduleEl.querySelector(
        ".e-date-header-wrap"
      ) as HTMLElement;
      const resourceWrap = scheduleEl.querySelector(
        ".e-resource-column-wrap"
      ) as HTMLElement;
      if (contentWrap && lastRestoredScrollPosition && restoredPendingScrollPosition) {
        contentWrap.scrollTop = lastRestoredScrollPosition.scrollTop;
        contentWrap.scrollLeft = lastRestoredScrollPosition.scrollLeft;
        if (resourceWrap) {
          resourceWrap.scrollTop = lastRestoredScrollPosition.scrollTop;
        }
        if (dateHeaderWrap) {
          dateHeaderWrap.scrollLeft = lastRestoredScrollPosition.scrollLeft;
        }
      }
      if (contentWrap && showFixedTimelineScrollbar.value) {
        contentWrap.style.overflowX = "auto";
        contentWrap.style.overflowY = "auto";
      }
      if (contentWrap && !(contentWrap as any).__scrollSyncBound) {
        (contentWrap as any).__scrollSyncBound = true;
        contentWrap.addEventListener(
          "scroll",
          () => {
            if (resourceWrap) {
              resourceWrap.scrollTop = contentWrap.scrollTop;
            }
            if (dateHeaderWrap) {
              dateHeaderWrap.scrollLeft = contentWrap.scrollLeft;
            }
            updateFixedTimelineScrollbar(contentWrap);
          },
          { passive: true }
        );
      }
      updateFixedTimelineScrollbar(contentWrap);
    }
  }

  if (activeView.value === "TimelineWeek" && props.dienstplan) {
    const schedule = getSchedule();
    applyTimelineWeekHoursFromEmployee();
    const appointments =
      schedule.ej2Instances.element.querySelectorAll(".e-appointment");
    // das darf ich nicht machen - da sonst beim Dienstplan immer beim ersteinstige alles ganz oben pickt!
    for (let y = 0; y < appointments.length; y++) {
      // appointments[i].style.top = "0%";
      (<HTMLElement>appointments[y]).style.minHeight = "64px";
      (<HTMLElement>appointments[y]).style.width = "";
    }
    // Syncfusion setzt bei rowAutoHeight die Zeilenhöhe pro Zeile dynamisch per Inline-Style
    // (updateCellHeight), abhängig vom real gerenderten Termin - kein fester Wert. Aus einer
    // bereits gemessenen Zeile übernehmen wir diese Höhe als CSS-Variable, damit leere Zeilen
    // (nur unser CSS-min-height) nie kleiner sind als eine Zeile mit Termin.
    const scheduleRoot = getDienstplanScheduleElement();
    if (scheduleRoot) {
      const measuredCell = Array.from(
        schedule.ej2Instances.element.querySelectorAll(".e-work-cells")
      ).find((cell) => (cell as HTMLElement).style.height) as
        | HTMLElement
        | undefined;
      if (measuredCell) {
        scheduleRoot.style.setProperty(
          "--dienstplan-row-height",
          measuredCell.style.height
        );
      }
    }
    if (pendingDienstplanFocusEvent.value) {
      // dataBound feuert bei aktivierter Ressourcen-Virtualisierung laufend beim Scrollen
      // mit - nur beim tatsächlichen Anlegen/Bearbeiten (shouldFocusPendingDienstplanOnDataBound)
      // erneut zum Termin scrollen, sonst würde jeder Scroll-Tick zurückspringen.
      if (shouldFocusPendingDienstplanOnDataBound.value) {
        shouldFocusPendingDienstplanOnDataBound.value = false;
        // retryScrollPendingDienstplanEventIntoView() zentriert den Termin bereits selbst
        // (horizontal + vertikal) - ein zusätzliches scrollToResource() hier würde die Zeile
        // zuerst an den oberen Rand ausrichten und dann sofort wieder zurückspringen.
        nextTick(() => {
          window.setTimeout(() => {
            retryScrollPendingDienstplanEventIntoView(null, 8);
            refreshFixedTimelineScrollbar();
          }, 200);
        });
      }
      return;
    }
    // Auf heute fokussieren wenn in der Woche, sonst auf Montag
    // renderDates[0] statt selectedDate.value: so ist weekBase garantiert in der gerenderten Woche
    const renderedStart =
      schedule.ej2Instances.activeView?.renderDates?.[0] ?? selectedDate.value;
    const scrollTarget = getTimelineWeekScrollTarget(renderedStart);
    const timelineScrollSignature = `${activeView.value}:${new Date(renderedStart).toDateString()}`;
    if (
      !restoredPendingScrollPosition &&
      lastTimelineWeekScrollSignature !== timelineScrollSignature
    ) {
      lastTimelineWeekScrollSignature = timelineScrollSignature;
      schedule.ej2Instances.scrollTo(
        startHour.value || startHourShow,
        scrollTarget
      );
    }
  } else if (activeView.value === "TimelineWeek") {
    // auch ohne dienstplan: heute wenn möglich, sonst Montag
    const scheduleDok = document.querySelector(".e-schedule");
    if (scheduleDok) {
      const scheduleObjLocal = (scheduleDok as any).ej2_instances[0];
      const renderedStart =
        scheduleObjLocal.activeView?.renderDates?.[0] ?? selectedDate.value;
      const scrollTarget = getTimelineWeekScrollTarget(renderedStart);
      const timelineScrollSignature = `${activeView.value}:${new Date(renderedStart).toDateString()}`;
      if (
        !restoredPendingScrollPosition &&
        lastTimelineWeekScrollSignature !== timelineScrollSignature
      ) {
        lastTimelineWeekScrollSignature = timelineScrollSignature;
        scheduleObjLocal.scrollTo(startHour.value || startHourShow, scrollTarget);
      }
    }
  }
  if (
    !restoredPendingScrollPosition &&
    (props.gotoAic ||
    goToAicLocal)
  ) {
    const resourceId = goToAicLocal || props.gotoAic;
    const scheduleDok = document.querySelector(".e-schedule");
    if (scheduleDok) {
      const scheduleObj = (scheduleDok as any).ej2_instances[0];
      // Bei TimelineWeek: heute wenn in der Woche, sonst Montag; sonst localDate
      const renderedStartGoto =
        scheduleObj.activeView?.renderDates?.[0] ?? selectedDate.value;
      const scrollTarget =
        activeView.value === "TimelineWeek"
          ? getTimelineWeekScrollTarget(renderedStartGoto)
          : activeView.value === "TimelineDay"
          ? getFirstDayOfWeek(new Date(selectedDate.value))
          : localDate;
      const gotoResourceScrollSignature = `${resourceId}:${activeView.value}:${new Date(renderedStartGoto).toDateString()}`;
      if (lastGotoResourceScrollSignature !== gotoResourceScrollSignature) {
        lastGotoResourceScrollSignature = gotoResourceScrollSignature;
        scheduleObj.scrollToResource(resourceId as any, "Employee"); // scrolling to specific resource
        scheduleObj.scrollTo(startHour.value || startHourShow, scrollTarget); // scrolling to specific date
      }
    }
    if (goToAicLocal) {
      // einmaliger "Dialog geschlossen -> zu diesem MA zurück"-Sprung - sonst würde
      // goToAicLocal jeden künftigen Zeitraumwechsel hijacken (siehe fillUpEvents finally-Block).
      goToAicLocal = 0;
    }
  }
  if (activeView.value === "Week" || activeView.value === "WorkWeek") {
    if (props.dienstplan) {
      // ohne das bleiben startHour/endHour auf dem schmalen Default (08:00-20:00) stehen,
      // wenn Week/WorkWeek direkt (ohne vorherigen Besuch von TimelineWeek) geöffnet wird -
      // der Tag passt dann komplett in die Höhe und es gibt (mangels Überlauf) gar nichts
      // zum vertikal Scrollen. TimelineWeek berechnet hier bereits die tatsächliche,
      // mitarbeiterabhängige Spanne - dieselbe Berechnung gilt fürs Stundenraster auch hier.
      applyTimelineWeekHoursFromEmployee();
      // Bei Week/WorkWeek ist startHour/endHour (anders als bei TimelineWeek) eine harte
      // Grenze des Rasters, kein reines Scroll-Ziel - echte Termine außerhalb der
      // vertraglichen Soll-Stunden (zB Spätdienst bis 20:00 bei einer Teilzeitkraft mit
      // Soll bis 13:00) würden sonst unsichtbar/nicht erreichbar bleiben.
      widenHoursFromDataSource();
    }
    if (scheduleDok) {
      const scheduleObj = (scheduleDok as any).ej2_instances[0];
      if (lastWeekViewScrollSignature !== activeView.value) {
        lastWeekViewScrollSignature = activeView.value;
        scheduleObj.scrollTo(startHour.value || startHourShow);
      }
    }
  }
  nextTick(() => {
    updateFixedTimelineScrollbar();
  });
}
function openStammdaten(aic: number) {
  const x = globalsKennung(Globals.Begriff, "LV_ReiseabrechnungAdmin", false);
  if (x) {
    onShowSite({
      title: "",
      site: "StammPerson",
      data: { aic },
      callOnLeave: () => {
        return true;
      },
    });
  }
}
function onReloadData() {
  loadResources(true);
}
function onActionBegin(args: any) {
  if (args.requestType === "toolbarItemRendering" && props.showICS) {
    // ICS Export Button
    const exportItem = {
      align: "Right",
      showTextOn: "Both",
      text: "\u21E9" + " ICS Export",
      click: onClickICS, // keine Bindings nötig in setup()
    };
    args.items.push(exportItem);
  }
}
function onPrintIconClick() {
  const schedule = getSchedule();
  const scheduleObj = schedule.ej2_instances;
  const schedule2 = scheduleObj.find(
    (inst: any) => typeof inst.print === "function"
  );
  if (schedule2) {
    schedule2.print();
  } else {
    console.warn("Schedule instance nicht verfügbar");
  }
}
function onClickICS() {
  const customData: any[] = [];
  const heute = new Date();
  let bisZeitraum = zeitbereich.bis;
  const vonZeitraum = zeitbereich.von; // soll der letzte Tag der ANGEZEIGT wird sein!
  const vonName = getMonthNames()[vonZeitraum.getMonth()];
  const vonYear = vonZeitraum.getFullYear().toString();
  let name = "Abwesenheiten " + vonName + " " + vonYear;
  if (activeView.value === "TimelineYear" || activeView.value === "Year") {
    // bei Year muss ich bis zum Jahresende künstlich rechnen! da es in dates NICHT drinnen steht!
    bisZeitraum = new Date(bisZeitraum.getFullYear(), 12, 0);
    name = "Abwesenheiten " + bisZeitraum.getFullYear().toString();
  }
  for (let i = 0; i < eventSettings.value.dataSource.length; i++) {
    const rowData = eventSettings.value.dataSource[i];
    const subject = rowData.IcsSubject;
    const icsExport = rowData.exportICS;
    const startTime = new Date(rowData.startDate);
    const endTime = new Date(rowData.EndTime);
    const isAllDay = rowData.IsAllDay;
    const categoryColor = rowData.CategoryColor;
    const description = rowData.Description;
    // const location = rowData.location;
    const zuBewilligen = rowData.Status;
    const bewilligt = rowData.bewilligt;
    const readOnly = false;
    const UID = rowData.aic_Bew_pool;
    // ab Heute und bis zum BIS des Zeitraums... nicht weiter in die Zukunft...
    if (
      ((bewilligt && zuBewilligen) || !zuBewilligen) &&
      icsExport &&
      startTime >= heute &&
      bisZeitraum &&
      startTime < bisZeitraum
    ) {
      customData.push({
        Subject: subject,
        StartTime: startTime,
        EndTime: endTime,
        DESCRIPTION: description,
        CategoryColor: categoryColor,
        ISREADONLY: readOnly,
        // LOCATION: location,
        UID,
      });
    }
  }
  if (customData?.length > 0) {
    exportToICalendarFromCustomDataArray(name, customData);
  } else {
    showSnackbar({
      text: globalsBegriffMemo(
        Globals.Begriff,
        "ICS_Export_leer",
        "Es wurden keine Daten für den ICS Export gefunden"
      ),
      color: "info",
    });
  }
}

// ICS-Exportfunktion für ein Array von Events
function exportToICalendarFromCustomDataArray(filename: string, events: any) {
  function formatDate(date: any) {
    const d = new Date(date);
    return d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  }

  let icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//WebAppVue3//EN",
  ];

  for (const event of events) {
    icsContent = icsContent.concat(
      [
        "BEGIN:VEVENT",
        `UID:${event.UID || Date.now() + "@webappvue3.local"}`,
        `DTSTAMP:${formatDate(new Date())}`,
        `DTSTART:${formatDate(event.StartTime)}`,
        `DTEND:${formatDate(event.EndTime)}`,
        `SUMMARY:${event.Subject || ""}`,
        `DESCRIPTION:${event.DESCRIPTION || ""}`,
        event.LOCATION ? `LOCATION:${event.LOCATION}` : "",
        "END:VEVENT",
      ].filter(Boolean)
    );
  }
  icsContent.push("END:VCALENDAR");
  const blob = new Blob([icsContent.join("\r\n")], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (filename || "export") + ".ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function openDruckVorschau(item: ALLDruck) {
  const printItem = item;
  const vecStamm = vector ?? [];
  const aicLocal = queryAIC || AUApi.Current.user.aic;

  if (vecStamm.length === 0 && !aicLocal) {
    showSnackbar({
      text: "Bitte wählen Sie zuerst einen Datensatz aus!",
      color: "warning",
    });
    return;
  }

  openDialog({
    title: printItem.bezeichnung,
    site: "BaseDruck",
    width: 500,
    fullscreen:
      typeof Constants.isMobile === "function"
        ? Constants.isMobile()
        : Boolean(Constants.isMobile),
    data: {
      zeitbereich,
      printItem,
      aic: aicLocal,
      vecStamm,
      noSelect: true,
      vectorPlanung: employee,
      ausPlanungVG: true,
    },
    onClose: () => {
      return true;
    },
  });
}
function onCalcStarted() {
  // Bei MDialog/userFrage darf das Menu nicht unmounten, sonst geht calculated verloren.
  calcMenuPersistent.value = true;
}

function onCalcBtn(args: any) {
  onCalculated(args);
  calcMenuPersistent.value = false;
  showCalcMenu.value = false;
}
function onCalculated(args: any) {
  onRefresh(true);
}
async function refreshScheduleData(args?: any, skipRemount: boolean = false) {
  if (props.queryMitarbeiter) {
    if (props.group2) {
      await loadResources(true, undefined, skipRemount);
    } else {
      await loadResources(false, undefined, skipRemount);
    }
  } else {
    await reloadData(skipRemount);
  }
}

async function onRefresh(x: boolean) {
  showLoading.value = true;
  // setTimeout(() => refreshScheduleData());
  await refreshScheduleData();
  showRefresh.value = false;
}
function onResizeStart(args: any) {
  args.scroll = { enable: false };
  updateMyHeight();
}
const onActionComplete = (args: any) => {
  // MA - sauber setzen - damit immer der richtige vorausgewählt ist!
  const scheduleDok = document.querySelector(".e-schedule");
  if (
    showDialog.value &&
    (args.requestType === "resourceExpanded" ||
      args.requestType === "resourceCollapsed" ||
      args.requestType === "resourceChanged")
  ) {
    return;
  }
  if (
    args.requestType === "resourceExpanded" ||
    args.requestType === "resourceCollapsed"
  ) {
    const scheduleObj = (scheduleDok as any)?.ej2_instances?.[0];
    const clickedResource =
      scheduleObj?.resourceBase?.lastResourceLevel?.[args.groupIndex];
    const resourceDetails = scheduleObj?.getResourcesByIndex?.(args.groupIndex);
    const parentGroupData = resourceDetails?.groupData;
    const resourceId = clickedResource?.groupOrder?.[0] ?? parentGroupData?.id;

    if (resourceId !== undefined && resourceId !== null) {
      setPersistedExpandedGroup(
        resourceId,
        args.requestType === "resourceExpanded"
      );
    }
  
  } else {
    if (args.requestType === "resourceChanged" && Constants.isMobile) {
      // Bei manuellem Mitarbeiterwechsel über die Compact-View (Handy) rendert Syncfusion
      // die bereits geladenen Termine für die neu gewählte Resource nicht automatisch neu -
      // der Schedule blieb dadurch leer, bis ein echter Zeitraumwechsel (navigating) einen
      // vollständigen Redraw erzwungen hat. NICHT über eine direkte imperative EJ2-API wie
      // scheduleInstance.refreshEvents() lösen - das umgeht den reaktiven :eventSettings-
      // Bindungspfad (setProperties -> dataBind) und hat laut refreshScheduleDataSourceWithPending()
      // (usePendingDienstplanSaves.ts) bereits zu doppelten/hängenden Termin-Elementen geführt.
      // Stattdessen denselben reaktiven Weg wie beim Speichern lokaler Vormerkungen nutzen.
      refreshScheduleDataSourceWithPending();
    }
    if (Constants.isMobile && props.showGroup && props.gotoAic) {
      if (savedGroupIndex || props.gotoAic || goToAicLocal)
        if (goToAicLocal) {
          const group: any = employee.findIndex((obj: any) => {
            return obj.GroupID === goToAicLocal;
          });
          // einmaliger "Dialog geschlossen -> zu diesem MA zurück"-Sprung - sonst würde
          // goToAicLocal (mobil) jeden künftigen Zeitraumwechsel hijacken und immer wieder
          // auf diesen MA zurückspringen statt beim inzwischen selbst gewählten MA zu bleiben.
          goToAicLocal = 0;
          if (group < 0) {
            return;
          }
          savedGroupIndex = group;
        } else if (props.gotoAic) {
          const group: any = employee.findIndex((obj: any) => {
            return obj.GroupID === props.gotoAic;
          });
          savedGroupIndex = group;
        }
      if (scheduleDok) {
        const scheduleObj2 = (scheduleDok as any).ej2_instances[0];
        const ej2Instance = scheduleObj2?.ej2Instances;
        if (ej2Instance) {
          currentGroupIndex = ej2Instance.uiStateValues.groupIndex;
          if (savedGroupIndex !== currentGroupIndex) {
            // wie im fillUpResource-finally-Block: im Mobile-Modus über die öffentliche API
            // wechseln, sonst bleibt der Resource-Header im Zwischenzustand hängen (grau,
            // Name unsichtbar), weil kein Re-Rendering ausgelöst wird.
            if (
              ej2Instance.uiStateValues.isGroupAdaptive &&
              typeof ej2Instance.selectResourceByIndex === "function"
            ) {
              ej2Instance.selectResourceByIndex(savedGroupIndex);
            } else {
              ej2Instance.uiStateValues.groupIndex = savedGroupIndex;
            }
            // gleiches Problem wie oben bei "resourceChanged": der Index-Wechsel allein
            // lässt Syncfusion die Termine der jetzt aktiven Resource nicht neu zeichnen
            // (zB Ersteinstieg mit gotoAic auf einen MA mit Daten - Schedule blieb leer,
            // bis man den Zeitraum wechselte).
            refreshScheduleDataSourceWithPending();
          }
        }
      }
    }
  }
};
function onCellClick(args: any) {
  if (Constants.isMobile) {
    if (readOnlyMutable.value || props.onlyEdit) {
      args.cancel = true;
    }
  } else {
    return;
  }
}
function normalizeWorkDays(rawValue: unknown): number[] {
  const rawDays = Array.isArray(rawValue)
    ? rawValue
    : typeof rawValue === "string"
    ? rawValue
        .replace(/[\[\]\s]/g, "")
        .split(",")
        .filter((value) => value !== "")
    : [];

  const normalizedDays: number[] = [];
  rawDays.forEach((value) => {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) {
      return;
    }

    let day = Math.trunc(parsedValue);
    if (day === 7) {
      day = 0;
    }

    if (day < 0 || day > 6 || normalizedDays.includes(day)) {
      return;
    }

    normalizedDays.push(day);
  });

  return normalizedDays.length > 0 ? normalizedDays : [0, 1, 2, 3, 4, 5, 6];
}

function parseHourStringToMinutes(value: unknown): number | null {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.getHours() * 60 + value.getMinutes();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value >= 0 && value <= 24) {
      return Math.round(value * 60);
    }
    if (value > 24 && value <= 24 * 60) {
      return Math.round(value);
    }
  }
  if (typeof value !== "string") {
    return null;
  }
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  if (hour < 0 || hour > 24 || minute < 0 || minute > 59) {
    return null;
  }
  if (hour === 24 && minute !== 0) {
    return null;
  }
  return hour * 60 + minute;
}

function formatMinutesToHourString(totalMinutes: number): string {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function applyTimelineWeekHoursFromEmployee() {
  if (!employee || employee.length === 0) {
    if (!startHour.value) {
      startHour.value = "08:00";
    }
    if (!endHour.value) {
      endHour.value = "20:00";
    }
    return;
  }
  const startMinutes = employee
    .map((t: any) => parseHourStringToMinutes(t.startHour))
    .filter((v: number | null): v is number => v !== null);
  const endMinutes = employee
    .map((t: any) => parseHourStringToMinutes(t.endHour))
    .filter((v: number | null): v is number => v !== null);
  if (startMinutes.length > 0) {
    startHour.value = formatMinutesToHourString(Math.min(...startMinutes));
  }
  if (endMinutes.length > 0) {
    endHour.value = formatMinutesToHourString(Math.max(...endMinutes));
  }
}

// employee.startHour/endHour sind die vertraglichen Soll-Stunden - tatsächlich eingetragene
// Termine (zB Überstunden/Spätdienst) können davon abweichen. Erweitert startHour/endHour
// deshalb nur nach AUSSEN (nie enger), damit im Stundenraster von Week/WorkWeek (harte Grenze,
// nicht nur Scroll-Ziel wie bei TimelineWeek) kein echter Termin außerhalb des sichtbaren
// Bereichs landet.
function widenHoursFromDataSource() {
  const dataSource = eventSettings.value?.dataSource;
  if (!dataSource || dataSource.length === 0) {
    return;
  }
  let minStart = parseHourStringToMinutes(startHour.value) ?? 0;
  let maxEnd = parseHourStringToMinutes(endHour.value) ?? 24 * 60;
  for (const ev of dataSource) {
    const eventStart: Date | undefined = ev.StartTime ?? ev.startDate;
    const eventEnd: Date | undefined = ev.EndTime ?? ev.endDate;
    if (eventStart instanceof Date && !isNaN(eventStart.getTime())) {
      minStart = Math.min(minStart, eventStart.getHours() * 60 + eventStart.getMinutes());
    }
    if (eventEnd instanceof Date && !isNaN(eventEnd.getTime())) {
      let endMinutesOfDay = eventEnd.getHours() * 60 + eventEnd.getMinutes();
      if (endMinutesOfDay === 0 && eventEnd.getTime() > (eventStart as Date)?.getTime()) {
        endMinutesOfDay = 24 * 60;
      }
      maxEnd = Math.max(maxEnd, endMinutesOfDay);
    }
  }
  startHour.value = formatMinutesToHourString(Math.max(0, minStart));
  endHour.value = formatMinutesToHourString(Math.min(24 * 60, maxEnd));
}
</script>

<template>
  <v-sheet
    min-width="200px"
    :loading="showLoading"
    v-if="initialized"
    :class="['schedule-shell', { 'schedule-shell-dienstplan': props.dienstplan }]"
    :style="{ '--dienstplan-pending-offset': dienstplanPendingLayoutOffset + 'px' }"
  >
    <v-toolbar class="pa-0 schedule-toolbar" v-if="group2 || showText">
      <span v-if="showText">
        {{ begriffBezeichnung("Web_keineDaten_gefunden", "keine Daten gefunden") }}
      </span>
      <v-checkbox
        v-if="group2 && showGroupMutable && initialized && !showText"
        :label="globalsBezeichnung(Globals.Begriff, 'Web_Gruppierung', 'Gruppierung')"
        @update:modelValue="onChange"
        density="compact"
        :hide-details="true"
        max-width="150px"
        v-model="checked"
      ></v-checkbox>
      <v-divider
        v-if="group2 && showSpringer"
        class="ml-5"
        vertical
        color="green"
      ></v-divider>
      <div
        style="font-size: 13px"
        class="e-label pl-5"
        v-if="dienstplan && group2 && showSpringer && initialized"
      >
        {{ begriffBezeichnung("Web_SpringerSetzen", "Springer setzen") }}
      </div>
      <EditComboBox
        v-if="dienstplan && group2 && showSpringer && initialized"
        id="springer"
        name="Springer"
        class="e-field pl-5 pt-5"
        style="width: 30%; max-width: 250px"
        query="Web_Favoriten_Dienstplan_Setzen"
        @onReloadData="onReloadData"
      />
      <v-spacer></v-spacer>
      <v-btn
        v-if="
          dienstplan &&
          initialized &&
          !props.formular?.pers &&
          (showRefresh || showLoading)
        "
        variant="text"
        @click="onRefresh(true)"
        :loading="showLoading"
        :disabled="showLoading"
      >
        <v-icon color="red">mdi-sync</v-icon></v-btn
      >
      <v-menu v-if="!showLoading && printItems && printItems.length > 0">
        <template v-slot:activator="{ props }">
          <v-btn icon="mdi-printer-outline" variant="text" v-bind="props"></v-btn>
        </template>
        <v-list lines="one" class="shortList" nav tile outlined elevation="12">
          <v-list-item
            class="pl-2"
            v-for="(printItem, ux) in printItems"
            :key="'print' + ux"
            @click="openDruckVorschau(printItem)"
          >
            <v-tooltip :disabled="!printItem.tooltip" location="bottom" :text="printItem.tooltip">
              <template v-slot:activator="{ props }">
                <v-list-item-title v-bind="props">
                  {{ printItem.bezeichnung }}
                </v-list-item-title>
              </template>
            </v-tooltip>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu
        v-model="showCalcMenu"
        v-if="!showLoading && modellItems && modellItems.length > 0"
        location="bottom"
        :close-on-content-click="false"
        :persistent="calcMenuPersistent"
      >
        <template v-slot:activator="{ props }">
          <v-btn variant="text" v-bind="props" icon="mdi-play-circle-outline"></v-btn>
        </template>
        <v-list class="shortList" nav tile outlined elevation="12">
          <v-list-item class="pl-2" v-for="(modellItem, ux) in modellItems" :key="'model' + ux">
            <CalcButton
              :text="modellItem.bezeichnung"
              :modell="modellItem.kennung"
              :icon="modellItem.icon ?? 'mdi-play-circle-outline'"
              :color="modellItem.farbe"
              variant="plain"
              :vecStamm="vector"
              :useVecStamm="true"
              @onCalculate="onCalcStarted"
              @calculated="onCalcBtn"
              :zeitbereich="zeitbereich"
              :bewAic="modellItem.bewegung ?? false"
            />
          </v-list-item>
        </v-list>
      </v-menu>
      <!-- <v-btn
        v-if="dienstplan && !formular?.pers"
        variant="text"
        @click="onPrintIconClick"
        color="green"
        icon="mdi-printer"
      >
      </v-btn> -->
      <CommonBaseFilter
        :key="filterKey"
        class="schedule-filter-action"
        colorUbergabe="grey darken-2"
        :queryData="queryData ?? undefined"
        ref="baseFilter"
        v-if="showFilter && showSchedule && queryData && showFilterComp"
        @filteredDataChanged="filteredDataChanged"
      />
      <BaseUserSettings
        v-if="!showLoading && groupAic"
        :aicBegriff="groupBegriff"
        :groupAic="groupAic"
        :checked="checked"
        @changeGroup="changeGroup"
      ></BaseUserSettings>
    </v-toolbar>
    <v-expand-transition>
      <v-sheet
        v-if="props.dienstplan && pendingDienstplanSaves.length > 0"
        class="dienstplan-pending-save-bar"
      >
        <div class="dienstplan-pending-save-info">
          <v-icon icon="mdi-content-save-clock-outline" size="20"></v-icon>
          <div>
            <strong v-if="savingPendingDienstplan">
              Speichere {{ savingPendingDienstplanDone }} von {{ savingPendingDienstplanTotal }}...
            </strong>
            <strong v-else>{{ pendingDienstplanSaves.length }} lokal vorgemerkt</strong>
            <span v-if="!savingPendingDienstplan">{{ pendingDienstplanSaves.map((item) => item.title).join(', ') }}</span>
          </div>
        </div>
        <div class="dienstplan-pending-save-actions">
          <v-btn
            density="compact"
            variant="text"
            color="grey-darken-1"
            :disabled="savingPendingDienstplan"
            @click="discardPendingDienstplanSaves"
          >
            Verwerfen
          </v-btn>
          <v-btn
            density="compact"
            color="warning"
            :loading="savingPendingDienstplan"
            @click="savePendingDienstplanSaves"
          >
            Alle speichern
          </v-btn>
        </div>
        <v-progress-linear
          v-if="savingPendingDienstplan"
          :model-value="(savingPendingDienstplanDone / Math.max(1, savingPendingDienstplanTotal)) * 100"
          color="warning"
          height="4"
          class="dienstplan-pending-save-progress"
        ></v-progress-linear>
      </v-sheet>
    </v-expand-transition>
    <!--       :currentView="activeView" string geht nicht auf View -->
    <div class="dienstplan-refresh-wrap">
    <ejs-schedule
      :key="setCompKey"
      id="Schedule"
      v-if="showSchedule"
      ref="scheduleObj"
      :enablePersistence="persistence"
      :activeView="activeView"
      :currentView="activeView"
      :eventSettings="eventSettings"
      :selectedDate="selectedDate"
      :showWeekNumber="showWeekNumber"
      :popupOpen="onPopupOpen"
      :readonly="readOnlyMutable"
      :eventRendered="onEventRendered"
      :showQuickInfo="showQuickInfoMutable"
      :quickInfoOnSelectionEnd="showQuickInfoMutable"
      @navigating="navigating"
      @actionBegin="onActionBegin"
      @actionComplete="onActionComplete"
      :cellClick="onCellClick"
      :firstDayOfWeek="1"
      :showTimeIndicator="true"
      :showLoading="true"
      :dateFormat="dateFormat"
      :cssClass="cssDesign"
      :allowDragAndDrop="allowDragAndDropMutable"
      :renderCell="onRenderCell"
      :group="group"
      :rowAutoHeight="true"
      :allowResizing="allowResizing"
      @resizeStart="onResizeStart"
      :height="scheduleHeight"
      :minDate="minDate"
      :dataBound="onDataBound"
      :timeScale="timeScale"
      :startHour="startHour"
      :endHour="endHour"
      :workDays="workDays"
      :resourceHeaderTemplate="'resourceHeaderTemplate'"
      :firstMonthOfYear="firstMonthValue"
      :monthsCount="monthsCount"
      :locale="localeRef"
    >
      <e-views>
        <e-view
          v-if="showWeek && !showGroupMutable && !props.dienstplan"
          option="Week"
          dateFormat="dd.MMM.yyyy"
          :allowVirtualScrolling="true"
        ></e-view>
        <e-view
          v-if="props.dienstplan && props.showTimeLineMonth"
          option="TimelineDay"
          :allowVirtualScrolling="true"
          :eventTemplate="'monthTemplate'"
        >
          <template v-slot:monthTemplate="{ data }">
            <div class="template-wrapper">
              <div class="subject" v-html="data.Subject"></div>
              <div class="location mt-n2" v-if="data.Location">
                {{ data.Location }}
              </div>
              <div
                class="description mt-n1"
                style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
                v-if="data.Description"
              >
                {{ globalsBezeichnung(Globals.Begriff, "Info", "Info") }}:
                {{ data.Description }}
              </div>
            </div>
          </template></e-view
        >

        <!-- <e-view
          v-if="showWeek && useCellTemplate && props.dienstplan"
          option="Week"
        ></e-view> -->
        <e-view
          v-if="
            props.showWeek &&
            props.useCellTemplate &&
            showGroupMutable &&
            props.dienstplan &&
            props.showTimeLineMonth
          "
          option="WorkWeek"
          :allowVirtualScrolling="true"
          :eventTemplate="'verticalEventTemplate'"
        >
          <template v-slot:["verticalEventTemplate"]="{ data }">
            <div class="vertical-event">
              <div class="vertical-content">
              
                <div class="vertical-line">
                  <div class="vertical-location" v-if="data.Location && !data.IsAllDay">
                    {{ data.LocationText + ": " + data.Location }}
                  </div>
                  <div class="vertical-desc" v-if="data.Description">
                    {{ globalsBezeichnung(Globals.Begriff, "Info", "Info") }}:
                    {{ data.Description }}
                  </div>
                </div>
                 <div class="vertical-line">
                  <div class="vertical-subject">{{ data.Subject }}</div>
                </div>
              </div>
            </div>
          </template></e-view
        >
        <e-view
          v-if="props.showWeek && useCellTemplate && props.dienstplan"
          option="Week"
          :allowVirtualScrolling="true"
          :eventTemplate="'verticalEventTemplate'"
        >
          <template v-slot:["verticalEventTemplate"]="{ data }">
            <div class="vertical-event">
              <div class="vertical-content">
              
                <div class="vertical-line">
                  <div class="vertical-location" v-if="data.Location && !data.IsAllDay">
                    {{ data.LocationText + ": " + data.Location }}
                  </div>
                  <div class="vertical-desc" v-if="data.Description">
                    {{ globalsBezeichnung(Globals.Begriff, "Info", "Info") }}:
                    {{ data.Description }}
                  </div>
                </div>
                  <div class="vertical-line">
                  <div class="vertical-subject">{{ data.Subject }}</div>
                </div>
              </div>
            </div>
          </template></e-view
        >
        <e-view
          v-if="showTimeLineMonth && !useCellTemplate && !props.dienstplan"
          option="TimelineMonth"
          :showWeekend="true"
          :allowVirtualScrolling="true"
          :eventTemplate="'monthTemplate'"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <div class="template-wrapper">
              <div class="subject" v-html="data.Subject"></div>
              <div class="location mt-n1" v-if="data.Location">
                {{ data.Location }}
              </div>
              <div
                class="description mt-n1"
                style="white-space: wrap"
                v-if="data.Description"
              >
                {{ data.Description }}
              </div>
            </div>
          </template></e-view
        >
        <e-view
          v-if="showWeek && !useCellTemplate && showGroupMutable && !props.dienstplan && !Constants.isMobile"
          option="TimelineWeek"
          :showWeekend="true"
          :allowVirtualScrolling="true"
        ></e-view>
        <e-view
          v-if="showWeek && showGroupMutable && props.dienstplan && !Constants.isMobile"
          option="TimelineWeek"
          :showWeekend="true"
          :allowVirtualScrolling="true"
          :eventTemplate="'monthTemplate'"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <div class="template-wrapper">
              <div class="subject" v-html="data.Subject"></div>
              <div class="location mt-n2" v-if="data.Location">
                {{ data.LocationText + ": " + data.Location }}
              </div>
              <div
                class="description mt-n1"
                style="white-space: wrap"
                v-if="data.Description"
              >
               {{ globalsBezeichnung(Globals.Begriff, "Info", "Info") }}:
                {{ data.Description }}
              </div>
            </div>
          </template>
        </e-view>
        <e-view
          v-if="showWeek && showGroupMutable && props.dienstplan && Constants.isMobile"
          option="WorkWeek"
          :allowVirtualScrolling="true"
          :eventTemplate="'verticalEventTemplate'"
        >
          <template v-slot:verticalEventTemplate="{ data }">
            <div class="vertical-event">
              <div class="vertical-content">
                <div class="vertical-line">
                  <div class="vertical-location" v-if="data.Location && !data.IsAllDay">
                    {{ data.LocationText + ": " + data.Location }}
                  </div>
                  <div class="vertical-desc" v-if="data.Description">
                    {{ globalsBezeichnung(Globals.Begriff, "Info", "Info") }}:
                    {{ data.Description }}
                  </div>
                </div>
                <div class="vertical-line">
                  <div class="vertical-subject">{{ data.Subject }}</div>
                </div>
              </div>
            </div>
          </template>
        </e-view>
        <e-view
          v-if="showWeek && useCellTemplate && showGroupMutable && !props.dienstplan && !Constants.isMobile"
          option="TimelineWeek"
          :showWeekend="true"
          :allowVirtualScrolling="true"
          :eventTemplate="'monthTemplate'"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <v-icon
              class="img centered-template-icon"
              :color="data.PrimaryColor"
              size="x-large"
              >mdi-shield-check</v-icon
            >
          </template>
        </e-view>
        <e-view
          v-if="showTimeLineMonth && useCellTemplate && !props.dienstplan"
          option="TimelineMonth"
          :showWeekend="true"
          :eventTemplate="'monthTemplate'"
          :allowVirtualScrolling="true"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <v-icon
              class="img centered-template-icon"
              :color="data.PrimaryColor"
              size="x-large"
              >mdi-shield-check</v-icon
            >
          </template>
        </e-view>
        <!-- <e-view
          v-if="props.showTimeLineMonth && !props.useCellTemplate && !props.dienstplan"
          option="TimelineMonth"
          :showWeekend="true"
          :allowVirtualScrolling="true"
        ></e-view> -->
        <e-view
          v-if="showMonth && !showTimeLineMonth && !useCellTemplate"
          option="Month"
          :showWeekend="true"
          :eventTemplate="'monthTemplate'"
          :allowVirtualScrolling="true"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <div class="template-wrapper">
              <div class="subject" v-html="data.Subject"></div>
              <div class="location mt-n2" v-if="data.Location">
                {{ data.Location }}
              </div>

              <div
                class="description mt-n1"
                style="white-space: wrap"
                v-if="data.Description"
              >
                {{ data.Description }}
              </div>
            </div>
          </template></e-view
        >
        <e-view
          v-if="showMonth && !showTimeLineMonth && useCellTemplate"
          option="Month"
          :showWeekend="true"
          :eventTemplate="'monthTemplate'"
          :allowVirtualScrolling="true"
        >
          <template v-slot:["monthTemplate"]="{ data }">
            <div>
              <v-icon
                class="img centered-template-icon"
                :color="data.PrimaryColor"
                size="x-large"
                >mdi-shield-check</v-icon
              >
            </div>
          </template>
        </e-view>
        <!-- :eventTemplate="takeCellTemplate" -->
        <!-- <e-view v-if="!showGroupMutable && showText" option="Year"></e-view> -->
        <e-view
          v-if="!showGroupMutable && !showText && showTimeLineYear"
          option="TimelineYear"
          :allowVirtualScrolling="true"
        ></e-view>
      </e-views>
      <template v-if="showGroupMutable" v-slot:["resourceHeaderTemplate"]="{ data }">
        <div class="template-wrap" v-if="dienstplan">
          <div
            class="resource-image"
            v-if="data.resourceData.showImage || data.resourceData.showAvatar"
            @click="openStammdaten(data.resourceData.GroupID)"
          >
            <v-img
              v-if="data.resourceData.showImage"
              class="resource-img"
              :src="data.resourceData.avatar"
              width="35px"
              height="35px"
            />
            <v-icon
              class="resource-img"
              dark
              v-if="data.resourceData.showAvatar"
              size="38"
              color="grey"
              >mdi-account-circle</v-icon
            >
          </div>

          <div class="resource-details">
            <div v-if="data.resourceData.Daten" class="resource-name-data">
              {{ data.resourceData.Text }}
              {{ data.resourceData.ZusatzText }}
            </div>
            <div
              v-if="!data.resourceData.Daten && data.resourceData.Designation"
              class="resource-name-text"
            >
              {{ data.resourceData.Text }}
              {{ data.resourceData.ZusatzText }}
            </div>
            <div
              v-if="!data.resourceData.Daten && !data.resourceData.Designation"
              class="resource-name"
            >
              {{ data.resourceData.Text }}
              {{ data.resourceData.ZusatzText }}
            </div>

            <div v-if="!dienstplan" class="resource-designation" style="color: black">
              {{ data.resourceData.Designation }}
            </div>
            <div v-if="dienstplan" style="display: flex; align-items: center; gap: 8px;">   
                <div class="resource-designation" style="color: black">
              {{ data.resourceData.Designation }}
            </div>
              <span
                v-if="data.resourceData.negativ > 0"
                class="resource-designation"
                style="color: red"
              >
                {{ data.resourceData.Urlaub }}
              </span>
              <span v-else class="resource-designation" style="color: green">
                {{ data.resourceData.Urlaub }}
              </span> </div>
            <div style="display: flex; align-items: center; gap: 8px;">
      
              <span v-if="data.resourceData.gesamtsaldo" class="resource-designation" style="color: #333; font-size: 0.95em;">
                {{ data.resourceData.gesamtsaldo }}
              </span>
      
            </div>
          </div>
        </div>
        <div class="template-wrap" v-else>
          <div
            class="resource-image"
            v-if="data.resourceData.showImage || data.resourceData.showAvatar"
            @click="openStammdaten(data.resourceData.GroupID)"
          >
            <v-img
              v-if="data.resourceData.showImage"
              class="resource-img"
              :src="data.resourceData.avatar"
              width="35px"
              height="35px"
            />
            <v-icon
              class="resource-img"
              dark
              v-if="data.resourceData.showAvatar"
              size="38"
              color="grey"
              >mdi-account-circle</v-icon
            >
          </div>

          <div class="resource-details">
            <div v-if="data.resourceData.Daten" class="resource-name-data">
              {{ data.resourceData.Text }} {{ data.resourceData.ZusatzText }}
            </div>
            <div
              v-if="!data.resourceData.Daten"
              class="resource-name"
              style="background-color: data.resourceData.ColorField"
            >
              {{ data.resourceData.Text }} {{ data.resourceData.ZusatzText }}
            </div>
            <div
              class="resource-designation"
              v-if="!data.resourceData.Urlaub"
              style="color: green"
            >
              {{ data.resourceData.Designation }}
            </div>
            <div class="resource-designation" style="color: blue">
              {{ data.resourceData.Urlaub }}
            </div>
          </div>
        </div>
      </template>
      <e-resources v-if="showGroupMutable">
        <e-resource
          v-if="showAbteilung"
          field="id"
          title="Choose"
          name="GroupData"
          :dataSource="groupData"
          textField="Text"
          idField="GroupID"
          expandedField="Expanded"
        >
        </e-resource>
        <e-resource
          groupIDField="groupIDField"
          textField="Text"
          idField="GroupID"
          workDaysField="workDays"
          startHourField="startHour"
          endHourField="endHour"
          name="Employee"
          title="Text"
          field="GroupID"
          :dataSource="employee"
          cssClassField="CssClass"
          :allowMultiple="allowMultiple"
        ></e-resource>
      </e-resources>
      <template v-slot:["tooltipTemplate"]="{ data }">
        <div class="tooltip-wrap" v-if="data">
          <div class="content-area">
            <div class="name" v-html="data.Subject"></div>
            <v-divider color="white"></v-divider>
            <div class="time" v-if="data.LocationText && data.Location">
              {{ data.LocationText }}: {{ data.Location }}
            </div>
            <div class="time" v-if="!data.LocationText && data.Location">
              {{
                globalsBezeichnung(Globals.Begriff, "Web_1Genehmigung", "1. Genehmigung")
              }}:
              {{ data.Location }}
            </div>
            <div class="time" v-if="data.Description">
              {{ globalsBezeichnung(Globals.Begriff, "Memo", "Memo") }}:
              {{ data.Description }}
            </div>
            <div class="time" v-if="data.Description2">
              {{
                globalsBezeichnung(Globals.Begriff, "Web_Memo_VG", "Memo Vorgesetzter")
              }}:
              {{ data.Description2 }}
            </div>
            <div class="time" v-if="data.text">{{ data.textText }}: {{ data.text }}</div>
          </div>
        </div>
      </template>
    </ejs-schedule>
    <v-fade-transition>
      <div
        v-if="savingPendingDienstplan"
        class="dienstplan-refresh-overlay"
      >
        <v-progress-circular indeterminate color="warning" size="32"></v-progress-circular>
      </div>
    </v-fade-transition>
    </div>
    <div v-if="showFixedTimelineScrollbar" class="fixed-timeline-scrollbar">
      <input
        type="range"
        min="0"
        :max="fixedTimelineScrollMax"
        :value="fixedTimelineScrollLeft"
        class="fixed-timeline-scrollbar-input"
        @input="onFixedTimelineScroll"
      />
    </div>
    <v-dialog
      v-if="showDialog"
      :fullscreen="showFullWidth"
      v-model="showDialog"
      class="dvhDialog"
      :persistent="showFullWidth"
    >
      <v-container id="container" grid-list-md text-center class="dvhDialog">
        <component
          :is="dialog.editorComponent"
          @save="onDialogSave"
          @localQueued="closeQueuedDienstplanDialog"
          @queueSaveLocal="queueDienstplanSave"
          @back="dialogBack"
          :siteData="{
            zeitbereich: dialog.zeitbereich,
            aic: dialog.aic,
            groupID: dialog.groupID,
            groupName: dialog.groupName,
            abwesenheit: dialog.abwesenheit,
            pendingDienstplanSaveId: dialog.pendingDienstplanSaveId,
            preloadedData: dialog.preloadedData,
            abwAicAbwesenheiten: dialog.abwAicAbwesenheiten,
          }"
          :lastValues="dialog.item"
          :aic="dialog.aic"
          :insertMode="dialog.insertMode"
          :queueSaveLocal="props.dienstplan"
          :backOnSave="false"
          @delete="onDelete"
          @deletePendingLocal="onDeletePendingLocal"
          :checkDateRange="checkDateRange"
          :checkOneEventPerDay="checkOneEventPerDay"
          @backDelete="onDialogSave"
        />
      </v-container>
    </v-dialog>
  </v-sheet>
</template>
<style>
.dvhDienstplan.e-schedule .e-content-wrap {
  /* nur für Week/WorkWeek - TimelineWeek braucht die volle Höhe, siehe Override unten */
  height: calc(40vh - var(--dienstplan-pending-offset, 10px)) !important;
  max-height: none !important;
  overflow-x: auto !important;
  padding-bottom: 42px;
}
@media (max-width: 550px) {
  .subject {
    white-space: pre-line;
    font-size: 12px;
  }
  .description {
    white-space: pre-line;
    font-size: 12px;
  }
  .dienstplan-pending-save-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .dienstplan-pending-save-actions {
    justify-content: flex-end;
  }
  .dvhDienstplan.e-schedule .e-content-wrap {
    height: 100% !important;
    max-height: none !important;
    overflow-x: auto !important;
    padding-bottom: 0px !important;
  }
  .dvhDienstplan.e-schedule .e-schedule-table {
    /* height: 100vh (Basis-Regel oben) deckelt die Tabelle hart auf exakt 100vh -
       unabhängig von der tatsächlich benötigten Höhe (zB Month-Ansicht mit mehreren
       Wochen). Syncfusion berechnet daraus offenbar beim allerersten Bind (vor jedem
       navigating-Zyklus) eine falsche/leere Sichtbarkeitsregion, wodurch der Schedule
       nach dem Ersteinstieg leer bleibt, obwohl die Daten korrekt geladen wurden - erst
       ein echter Zeitraumwechsel (navigating) erzwingt eine Neuberechnung. */
    height: auto !important;
  }
}

.e-hide-add-button {
  display: none !important;
}
.dienstplan-pending-save-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(245, 158, 11, 0.28);
  background: #fff8e8;
  color: #473415;
}

.dienstplan-pending-save-info {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.dienstplan-pending-save-info strong,
.dienstplan-pending-save-info span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dienstplan-pending-save-info strong {
  font-size: 0.82rem;
}

.dienstplan-pending-save-info span {
  max-width: min(60vw, 680px);
  color: #7a5a1e;
  font-size: 0.72rem;
}

.dienstplan-pending-save-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
}

.dienstplan-pending-save-progress {
  /* absolut auf der unteren Kante der Leiste positioniert statt als zusätzliches Flex-Kind -
     sonst wächst die Leiste beim Start des Speicherns um die Höhe der Progress-Bar und
     verschiebt den Schedule-Inhalt darunter sichtbar ("springt"). */
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  width: auto;
}

.dienstplan-refresh-wrap {
  position: relative;
}

/* Syncfusion räumt bei einem dataSource-Wechsel alle Termin-Elemente ab und baut sie neu
   auf - bei einer großen Ansicht (viele Mitarbeiter/Termine, zB nach "Alle speichern")
   dauert das sichtbar lange genug, um wie ein Aus-/Wiedereinblenden zu wirken. Statt das
   zu verhindern (nicht zuverlässig möglich), wird es hinter einer Ladeanzeige kaschiert. */
.dienstplan-refresh-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
}

.dienstplan-local-pending-event {
  overflow: hidden !important;
  border: 1px dashed #d97806 !important;
  background: orange !important;
  color: #473415 !important;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.86),
    0 3px 9px rgba(154, 93, 8, 0.2) !important;
}

.dienstplan-local-pending-event-focus {
  outline: 3px solid rgba(245, 158, 11, 0.34) !important;
  outline-offset: 2px;
}

.dienstplan-local-pending-event .template-wrapper,
.dienstplan-local-pending-event .vertical-event {
  color: #473415 !important;
}

.dienstplan-local-pending-event::after {
  content: "Entwurf";
  position: absolute;
  right: 4px;
  bottom: 2px;
  padding: 0 6px;
 
  background: #d97806;
  color: #ffffff;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 14px;
  letter-spacing: 0;
}
/* delete Button am handy bei MiniDialog - immer wegschalten */
.e-quick-popup-wrapper.e-device
  .e-multiple-event-popup
  .e-popup-header
  .e-delete-icon {
  display: none;
}

/* beim Handy muss das kleine Popupfenster für Edit - Event unter die AppBar gelegt werden! */
.e-quick-popup-wrapper {
  top: calc(50px + constant(safe-area-inset-top)) !important; /* iOS < 11.2 */
  top: calc(50px + env(safe-area-inset-top)) !important;
}
.schedule .e-schedule .e-timeline-month-view .e-resource-cells .e-parent-node {
  background-color: rgb(237, 194, 102);
}
/* templates */
.template .img {
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}
.centered-template-icon {
  font-size: 36px;
  margin-top: 12px;
  margin-left: 8px;
}
.resource-image {
  width: 45px;
  height: 40px;
  float: left;
  border-radius: 50%;
  margin-right: 2px;
}

.resource-name {
  font-size: 13px;
  color: #000;
}
.resource-name-data {
  font-size: 13px;
  color: orange;
}
.resource-name-text {
  font-size: 13px;
  color: green;
}
.resource-designation {
  font-size: 11px;
  color: #000;
}
.resource-img {
  border-radius: 50%;
  padding: 0 0 0 0;
  margin: 0 0 0 0;
}
.dvh .e-sunday {
  background-color: #f5e5e6 !important;
}

.dvh .e-monday {
  background-color: #e7eef6 !important;
}

.dvh .e-tuesday {
  background-color: #e8eaf6 !important;
}

.dvh .e-wednesday {
  background-color: #e3f2fd !important;
}

.dvh .e-thursday {
  background-color: #e1f5fe !important;
}

.dvh .e-friday {
  background-color: #e0f7fa !important;
}

.dvh .e-saturday {
  background-color: #e0f2f1 !important;
}
.e-schedule .e-timeline-view .e-date-header-wrap table tbody td,
.e-schedule .e-timeline-month-view .e-date-header-wrap table tbody td {
  padding: 2px !important;
}
.e-appointment {
  min-height: 60px !important;
  margin: 1px;
}

.dvhDienstplan.e-schedule .e-timeline-view .e-content-wrap {
  /* 100vh ist die gesamte Bildschirmhöhe minus der Platz oberhalb des Schedulers */
  height: calc(100vh - 320px - var(--dienstplan-pending-offset, 0px)) !important;
}
.dvhDienstplan.e-treeview .e-list-item.e-active > .e-fullrow {
    background-color: transparent;
    border-color: transparent;
}
.dvhDienstplan.e-treeview .e-list-item.e-active {
    background: #eee;
}
.dvh.e-treeview .e-list-item.e-active > .e-fullrow {
    background-color: transparent;
    border-color: transparent;
}
.dvh.e-treeview .e-list-item.e-active {
    background: #eee;
}
.fixed-timeline-scrollbar {
  background: #ffffff;
  border: 0;
  box-shadow: 0 -3px 12px rgba(32, 49, 60, 0.08);
  margin-top: 0;
  position: sticky;
  bottom: 0;
  padding: 4px 8px;
  width: 100%;
  z-index: 4;
  margin-top: -5px;
}
.fixed-timeline-scrollbar-input {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  display: block;
  height: 12px;
  width: 100%;
}
.fixed-timeline-scrollbar-input::-webkit-slider-runnable-track {
  background: #dfe8ee;
  border-radius: 999px;
  height: 8px;
}
.fixed-timeline-scrollbar-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  background: #245271;
  border: 0;
  border-radius: 999px;
  height: 12px;
  margin-top: -2px;
  width: 72px;
}
.fixed-timeline-scrollbar-input::-webkit-slider-thumb:hover {
  background: #1b425d;
}
.fixed-timeline-scrollbar-input::-moz-range-track {
  background: #dfe8ee;
  border: 0;
  border-radius: 999px;
  height: 8px;
}
.fixed-timeline-scrollbar-input::-moz-range-thumb {
  background: #245271;
  border: 0;
  border-radius: 999px;
  height: 12px;
  width: 72px;
}
.dvhDienstplan.e-appointment {
  height: 50px !important;
  min-height: 50px !important;
  max-height: 50px !important;
  margin: 0px;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-appointment,
.e-schedule .e-timeline-month-view .e-appointment {
  height: 50px !important;
  min-height: 50px !important;
  max-height: 50px !important;
  margin: 0px;
}
.dvhDienstplan
  .e-schedule
  .e-vertical-view
  .e-all-day-appointment-wrapper
  .e-appointment {
  background: #3f51b5;
  border: 1px solid #e8eaf6;
  border-radius: 2px;
  color: #fff;
  display: -ms-flexbox;
  display: -webkit-box;
  display: flex;
  height: 22px;
  overflow: hidden;
  position: absolute;
  min-height: 30px !important;
}
.dvhDienstplan.e-schedule
  .e-vertical-view
  .e-all-day-appointment-wrapper
  .e-appointment
  .e-appointment-details
  .e-subject {
  -ms-flex: auto;
  -webkit-box-flex: 1;
  flex: auto;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: clip !important;
  margin: 0px;
}
.vertical-event {
  height: 100%;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start !important;
  align-items: flex-start !important;
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  margin: 0px;
}
.vertical-content {
  display: flex;
  flex-direction: row;
  gap: 1px;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.vertical-subject {
  writing-mode: vertical-lr; /* Text top->bottom, Zeilen links->rechts */
  text-orientation: mixed;
  white-space: nowrap;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  min-width: 14px;
  max-height: 100%;
}

.vertical-line {
  display: flex;
  flex-direction: column;
  align-items: flex-start !important;
  gap: 2px;
  overflow: hidden;
  flex-shrink: 1;
  min-height: 0;
}
.vertical-line:first-child {
  order: 0;
  flex: 0 0 13px;
  min-width: 13px;
}
.vertical-line:last-child {
  order: 1;
  flex: 0 0 14px;
  min-width: 14px;
}
/* Zusatzinfos nur anzeigen, wenn genug Platz (z. B. per JS/CSS zusätzliche Klasse setzen) */
.vertical-location,
.vertical-desc {
  writing-mode: vertical-lr;
  white-space: nowrap;
  font-weight: 400;
  font-size: 11px;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Wenn Appointment groß genug ist, kann eine Klasse e-vertical-large gesetzt werden,
   dann die Zusatzinfos anzeigen */
.dvhDienstplan .e-appointment.e-vertical-large .vertical-location,
.dvhDienstplan .e-appointment.e-vertical-large .vertical-desc {
  display: block;
}

/* Appointment-Elemente im Dienstplan: Timeline overflow sichtbar, Vertical-View hidden */
.dvhDienstplan .e-timeline-view .e-appointment,
.dvhDienstplan .e-timeline-month-view .e-appointment {
  overflow: visible !important;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0 !important;
  margin: 0 !important;
}
.dvhDienstplan .e-vertical-view .e-appointment {
  overflow: hidden !important;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0 !important;
  margin: 0 !important;
}
/* Syncfusion Default-Elemente und Overflow-Indikatoren im Vertical-View ausblenden */
.dvhDienstplan .e-vertical-view .e-appointment .e-left-icon,
.dvhDienstplan .e-vertical-view .e-appointment .e-right-icon,
.dvhDienstplan .e-vertical-view .e-appointment .e-up-icon,
.dvhDienstplan .e-vertical-view .e-appointment .e-down-icon,
.dvhDienstplan .e-vertical-view .e-appointment .e-inner-wrap > .e-subject {
  display: none !important;
}
.dvhDienstplan .e-vertical-view .e-appointment .e-appointment-details {
  overflow: hidden !important;
  width: 100%;
  height: 100%;
  padding: 2px !important;
}

.dvhDienstplan
  .groupediting.e-schedule
  .e-month-view
  .e-appointment
  .e-appointment-details {
  padding: 1px;
  padding-left: 3px;
  margin: 0 !important;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-resource-cells.e-parent-node,
.dvhDienstplan.e-schedule
  .e-timeline-view
  .e-work-cells.e-resource-group-cells {
  height: 50px !important;
  background: #43a047 !important;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-work-cells,
.dvhDienstplan.e-schedule .e-timeline-month-view .e-work-cells {
  /* an die Höhe einer Zeile mit einem echten Termin angleichen - Syncfusion setzt diese
     Höhe pro Zeile dynamisch per Inline-Style (updateCellHeight), abhängig vom real
     gerenderten Termin. --dienstplan-row-height wird in onDataBound() (baseSyncSchedule.vue)
     aus einer bereits gemessenen Zeile aktuell gehalten, damit leere Zeilen nie kleiner
     sind als eine Zeile mit Termin ("springt" sonst beim ersten Speichern). */
  min-height: var(--dienstplan-row-height, 68px) !important;
  width: 40px;
}
.dvh.schedule .e-schedule .e-timeline-view .e-work-cells,
.dvh.schedule .e-schedule .e-timeline-month-view .e-work-cells {
  min-height: 70px !important;
}

.e-schedule .e-timeline-view .dienstplan-day-start-cell,
.e-schedule .e-timeline-view .e-header-row .dienstplan-day-start-cell,
.e-schedule .e-timeline-view .e-time-slots.dienstplan-day-start-cell {
  border-left: 2px solid rgba(0, 0, 0, 0.4) !important;
}

.dvh.schedule .e-schedule .e-timeline-view .e-resource-cells,
.dvh.schedule .e-schedule .e-timeline-month-view .e-resource-cells {
  /* background-color: #aacadf !important; */
  border-color: rgba(0, 0, 0, 0.12);
  border-style: solid;
  border-width: 0 1px 1px 0;
  color: rgba(0, 0, 0, 0.87);
  min-height: 71px !important;
  padding-left: 15px;
  padding-right: 0;
  vertical-align: middle;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-resource-cells,
.dvhDienstplan.e-schedule .e-timeline-month-view .e-resource-cells {
  background-color: #fafafa;
  border-color: rgba(0, 0, 0, 0.12);
  border-style: solid;
  border-width: 0 1px 1px 0;
  color: rgba(0, 0, 0, 0.87);
  /* mit e-work-cells oben synchron halten, sonst driften Zeile und Resource-Label
     auseinander sobald eine Zeile durch einen Termin wächst */
  min-height: var(--dienstplan-row-height, 68px) !important;
  padding-left: 15px;
  padding-right: 0;
  vertical-align: middle;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-resource-cells, .e-schedule .e-timeline-month-view .e-resource-cells {
    background: #fafafa;
    border-color: rgba(0, 0, 0, 0.12);
    border-style: solid;
    border-width: 0 1px 1px 0;
    color: rgba(0, 0, 0, 0.87);
    height: 70px !important;
    padding-left: 15px;
    padding-right: 0;
    vertical-align: middle;
}
.dvhDienstplan.e-schedule .e-timeline-view .e-work-cells, .e-schedule .e-timeline-month-view .e-work-cells {
    height: 70px !important;
}
.dvhDienstplan.e-schedule .e-vertical-view .e-resource-cells {
  background-color: #fafafa;
  border-color: rgba(0, 0, 0, 0.12);
  border-style: solid;
  border-width: 0 1px 1px 0;
  color: rgba(0, 0, 0, 0.87);
  height: auto;
  padding-left: 15px;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
  vertical-align: middle;
  height: 90px !important;
}

.dvhDienstplan .templatewrap {
  text-align: left;
  width: 100%;
  height: 100%;
  overflow-wrap: break-word;
  padding-top: 4px;
  white-space: normal;
  word-wrap: break-word;
}
.templatewrap img {
  width: 20px;
  height: 20px;
}

.dvhDienstplan .weekend {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.05) !important;
}
.dvhDienstplan.e-schedule
  .e-timeline-view
  .e-resource-left-td
  .e-resource-text {
  display: flex;
  font-weight: 500;
  padding: 0;
  /* height: 140px !important;*/
}
</style>
<style scoped>
.dvh.e-schedule .e-schedule-toolbar .e-icons {
  font-family: "Schedule_print", "e-icons";
}

.dvh.e-schedule .e-schedule-toolbar .e-icon-schedule-print::before {
  content: "\e204";
}

.dvh.e-schedule .e-schedule-toolbar .e-toolbar-item.e-today {
  display: none;
}

.dvhDialog {
  padding: 0px;
  width: 450px;
  /* NOTCH bei Iphone umgehen */
  padding-top: 20px;
  padding-bottom: 20px;
  padding-top: constant(safe-area-inset-top, 2px);
  /* Status bar height on iOS 11+ */
  padding-top: env(safe-area-inset-top, 2px);
  padding-bottom: constant(safe-area-inset-top);
  /* Status bar height on iOS 11+ */
  padding-bottom: env(safe-area-inset-top);
}

.dvh {
  min-width: 500px;

  width: 100%;
}
.dvhDienstplan.e-schedule .e-schedule-table {
  border: 0 none;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  table-layout: fixed;
  width: 100%;
  height: 100vh;
}
.dvh.e-schedule .e-schedule-table {
  border: 0 none;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  table-layout: fixed;
  width: 100%;
  height: 100vh;
}

.dvh.e-schedule .e-read-only {
  opacity: 0.5;
}

.dvh.e-schedule .e-vertical-view .e-day-wrapper .e-appointment.e-past-app {
  background-color: #d2691e;
}
.dvh.e-schedule .e-date-header-wrap .e-schedule-table thead {
  display: none;
}

.dvh.e-schedule .e-date-header div {
  display: inline-grid !important;
  width: -webkit-fill-available;
  height: fit-content !important;
  text-align: center;
}

@media (max-width: 550px) {
  .dvh {
    min-width: 300px;
    min-height: 450px;
    width: 100%;
    height: 100%;
  }
  .dvhDienstplan {
    min-width: 300px;
    min-height: 450px;
    width: 100%;
    height: 100%;
  }
  .dvhDienstplan.e-treeview .e-list-item.e-active > .e-fullrow {
    background-color: transparent;
    border-color: transparent;
  }
  .dvhDienstplan.e-treeview .e-list-item.e-active {
    background: #eee;
  }
  .dvh.e-treeview .e-list-item.e-active > .e-fullrow {
    background-color: transparent;
    border-color: transparent;
    }
  .dvh.e-treeview .e-list-item.e-active {
    background: #eee;
  }
  .dvhDialog {
    padding: 0px;
    width: 100%;
    /* NOTCH bei Iphone umgehen */
    padding-top: 20px;
    padding-bottom: 20px;
    padding-top: constant(safe-area-inset-top, 2px);
    /* Status bar height on iOS 11+ */
    padding-top: env(safe-area-inset-top, 2px);
    padding-bottom: constant(safe-area-inset-top);
    /* Status bar height on iOS 11+ */
    padding-bottom: env(safe-area-inset-top);
  }

  .e-schedule .e-month-view .e-work-cells {
    height: auto;
    vertical-align: top;
    max-width: auto;
  }

  .e-schedule .e-timeline-view .e-work-cells {
    position: relative;
  }

  .dvh.e-schedule .e-timeline-view .e-resource-left-td {
    width: 100px;
  }

  /* Employee-Name im Dienstplan (mobil): grauer Hintergrund entfernt, da er über dem
     Namenstext lag und ihn schlecht lesbar machte - stattdessen Rahmen für JEDEN
     Mitarbeiternamen, nicht nur für eine ausgewählte Zeile. */
  .dvh.e-schedule .e-resource-cells,
  .dvh.e-schedule .e-vertical-view .e-resource-cells,
  .dvh.e-schedule .e-vertical-view .e-selected-cell,
  .dvhDienstplan.e-schedule .e-resource-cells,
  .dvhDienstplan.e-schedule .e-vertical-view .e-resource-cells,
  .dvhDienstplan.e-schedule .e-vertical-view .e-selected-cell {
    background-color: transparent !important;
    background: transparent !important;
  }

  .dvh .resource-name,
  .dvh .resource-name-data,
  .dvh .resource-name-text,
  .dvhDienstplan .resource-name,
  .dvhDienstplan .resource-name-data,
  .dvhDienstplan .resource-name-text {
    display: inline-block;
    background-color: transparent !important;
    border: 1px solid rgba(0, 0, 0, 0.35);
    border-radius: 4px;
    padding: 2px 6px;
  }

}

/* am Handy geht bei EDIT ein PopUp Fenster auf - mit dem CSS kann ich es runter rutschen - wegen NOTCH */
.dvh .e-quick-popup-wrapper.e-device {
  top: 20px !important;
}
.dvh.e-quick-popup-wrapper .e-event-popup .e-custom-row {
  color: #212529;
  display: -ms-flexbox;
  display: flex;
  padding-top: 10px;
}

.dvh.e-quick-popup-wrapper .e-event-popup .e-custom-icon {
  color: #495057;
  font-size: 18px;
  padding-right: 13px;
  font-size: 16px;
  padding: 2px 10px 0 0;
}

.dvh.e-quick-popup-wrapper .e-event-popup .e-custom-status {
  color: #212529;
  cursor: default;
  font-size: 14px;
}

.dvh.e-quick-popup-wrapper .e-custom-icon::before {
  content: "\e77d";
}

.dvh .e-custom-icon .e-custom-icon::before {
  content: "\e77d";
}

.dvh.e-schedule .e-employee-cells.e-child-node .employee-category,
.block-events.e-schedule .e-employee-cells.e-child-node .employee-name {
  padding: 5px;
}

.cssPersonlich {
  background-color: green;
}

.cssAlle {
  background-color: lightblue;
  max-width: 50px;
}

.dvh .block-events.e-schedule .template-wrap {
  display: flex;
  text-align: left;
  height: 100%;
  line-height: 57px;
}
.dvh .templatewrap {
  text-align: center;
  width: 100%;
  height: 100%;
}
.dvhDienstplan .templatewrap {
  text-align: left;
  width: 100%;
  height: 100%;
  overflow-wrap: break-word;
  padding-top: 4px;
  white-space: normal;
  word-wrap: break-word;
}
.templatewrap img {
  width: 20px;
  height: 20px;
}

.dvhDienstplan .weekend {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.05) !important;
}
.dvhDienstplan.e-schedule
  .e-timeline-view
  .e-resource-left-td
  .e-resource-text {
  display: flex;
  font-weight: 500;
  padding: 0;
  height: 140px !important;
}
</style>
